import { MeetingPollHistoryPanel } from "pages/Meetings/MeetingPollHistoryPanel"
import type { ChangeEvent } from "react"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, useLayoutEffect, useSyncExternalStore } from "react"
import { useNavigate } from "react-router-dom"
import { getMeetingUrl, openMeetingEntry } from "utils/meetingEntry"
import { Badge } from "components/ui/badge"
import { Button } from "components/ui/button"
import { AnimatedDialog } from "components/ui/animated-dialog"
import { Checkbox } from "components/ui/checkbox"
import { DialogDescription, DialogHeader, DialogTitle } from "components/ui/dialog"
import { Input } from "components/ui/input"
import { Textarea } from "components/ui/textarea"
import { Label } from "components/ui/label"
import { Spinner } from "components/ui/spinner"
import { useTimezone } from "contexts/TimezoneContext"
import {
  Check,
  ChevronDown,
  Clock,
  Copy,
  Download,
  Edit3,
  FileText,
  Film,
  MapPin,
  Link2,
  MessageSquare,
  Plus,
  Repeat,
  Search,
  Send,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
  Video,
  X,
} from "lucide-react"
import { images, media, meetings, pdfs, videos } from "services/api"
import type {
  MeetingAttendanceEntry,
  ManualGuestAttendee,
  MeetingQuestionSubmission,
  MeetingQuestionSubmissionStatus,
  MeetingInspireActivity,
  MeetingInspireResponse,
  MeetingResponse,
  RecurrenceResponse,
} from "services/api/meetingsClient"
import type { CohortRole } from "types"
import type { CohortRoleLabelSource } from "utils/cohortRoleLabels"
import { getInviteeBucketLabel } from "utils/cohortRoleLabels"
import { getDailyCheckInOpen, subscribeToDailyCheckInVisibility } from "utils/dailyCheckIn"
import { isCurrentUserMeetingParticipant } from "components/mentorship/meetingInvitation"
import type { MentorshipRelationship } from "services/api/mentorshipContinuityClient"
import { relationshipEventsFor } from "components/mentorship/CohortRelationshipSpotlight"
import { meetingDateKey, meetingTimeZoneLabel, resolveMeetingTimeZone } from "utils/meetingTime"

export type EventsListItem = {
  id: string
  title: string
  date: string
  time?: string
  duration?: string
  status: "upcoming" | "past" | "cancelled"
  isVirtual?: boolean
  isMilestone?: boolean
  guestAccessEnabled?: boolean
  waitingRoomEnabled?: boolean
  guestJoinUrl?: string | null
  canJoinVideo?: boolean
  activeParticipantCount?: number
  lateJoinUntil?: string | null
  manuallyEndedAt?: string | null
  questionCollectionEnabled?: boolean
  questionSubmissionCount?: number
  unreviewedQuestionSubmissionCount?: number
  canReviewQuestions?: boolean
  attendanceTracked?: boolean
  virtualLink?: string
  transcriptionStatus?: "pending" | "started" | "available" | "failed"
  transcriptText?: string | null
  summaryText?: string | null
  recordingEnabled?: boolean
  recordingStatus?: "pending" | "copying" | "available" | "failed" | null
  recordingAvailable?: boolean
  timeZoneAbbr?: string
  timeZone?: string
  startTimeIso?: string
  endTimeIso?: string
  description?: string
  location?: string
  cohortId?: string | null
  invitedUserIds?: Array<string | number>
  inviteesMode?: InviteesMode
  createdById?: string
  type?: string
  attachments?: Attachment[]
  recurrence?: RecurrenceRule | null
  recurrenceSeriesId?: string | null
  isException?: boolean
  occurrenceStatus?: "normal" | "edited" | "cancelled"
  cancelled?: boolean
}

export type Attachment = {
  key: string
  url?: string
  name: string
  size?: number
  mime?: string
  assetId?: string
  originalFilename?: string | null
}

type WeekDay = "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday"

type RecurrenceRule = {
  interval: number
  period: "day" | "week" | "month"
  daysOfWeek?: WeekDay[]
  startDate?: string
  endDate?: string
  noEndDate?: boolean
  materializedUntil?: string
}

export type InviteesMode = "cohort" | "mentors" | "scholars" | "custom"
type InviteeSelectionMode = InviteesMode | ""
type PopupState = "upcoming" | "soon" | "live" | "rejoin" | "past"
type RecurrenceEditScope = "occurrence" | "following" | "series"
type RecurrenceScopeChooserKind = "edit" | "cancel"
type RosterMember = {
  id: string | number
  name: string
  role?: string
  roleKey?: CohortRole
  avatarUrl?: string | null
  avatar_url?: string | null
  profile?: {
    avatar_url?: string | null
  }
}

const isVisibleMeetingEvent = (event: EventsListItem) =>
  event.status !== "cancelled" && event.occurrenceStatus !== "cancelled" && !event.cancelled

export function getNearbyMeetingAdvice({ events, start, end, timeZone, editingId, cohortId, participantIds }: {
  events: EventsListItem[]
  start: Date
  end: Date
  timeZone: string
  editingId?: string
  cohortId?: string | null
  participantIds: string[]
}) {
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) return []
  const day = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
  const neighbors = events.filter(event => {
    if (!isVisibleMeetingEvent(event) || event.id === editingId || (cohortId && String(event.cohortId) !== String(cohortId))) return false
    if (event.inviteesMode === 'custom' && participantIds.length > 0) {
      const invited = new Set([...(event.invitedUserIds || []).map(String), String(event.createdById || '')])
      if (!participantIds.some(id => invited.has(id))) return false
    }
    return Boolean(event.startTimeIso && event.endTimeIso)
  }).map(event => ({ event, start: new Date(event.startTimeIso!), end: new Date(event.endTimeIso!) }))
    .filter(row => Number.isFinite(row.start.getTime()) && Number.isFinite(row.end.getTime()) && row.end > row.start)
    .filter(row => (row.start < end && row.end > start) || day.format(row.start) === day.format(start) || day.format(row.end) === day.format(end))
  const overlaps = neighbors.filter(row => row.start < end && row.end > start)
  const previous = neighbors.filter(row => row.end <= start).sort((a, b) => b.end.getTime() - a.end.getTime())[0]
  const next = neighbors.filter(row => row.start >= end).sort((a, b) => a.start.getTime() - b.start.getTime())[0]
  const advice: Array<{ event: EventsListItem; overlap: boolean; message: string }> = overlaps.map(row => ({ event: row.event, overlap: true, message: `Overlaps with ${row.event.title}` }))
  if (previous) {
    const gap = Math.round((start.getTime() - previous.end.getTime()) / 60000)
    advice.push({ event: previous.event, overlap: false, message: gap === 0 ? `Starts immediately after ${previous.event.title}` : `${gap} ${gap === 1 ? 'minute' : 'minutes'} after ${previous.event.title}` })
  }
  if (next) {
    const gap = Math.round((next.start.getTime() - end.getTime()) / 60000)
    advice.push({ event: next.event, overlap: false, message: gap === 0 ? `Ends immediately before ${next.event.title}` : `${gap} ${gap === 1 ? 'minute' : 'minutes'} before ${next.event.title}` })
  }
  return advice
}

const recurrenceScopeCopy: Record<RecurrenceScopeChooserKind, {
  title: string
  body: string
  confirm: string
  dismiss: string
  options: Array<{ key: RecurrenceEditScope; label: string; desc: string }>
}> = {
  edit: {
    title: "Edit recurring meeting",
    body: "Choose whether to update only this meeting or the recurring series.",
    confirm: "Continue",
    dismiss: "Cancel",
    options: [
      { key: "occurrence", label: "This occurrence", desc: "Updates only this meeting." },
      { key: "following", label: "This and following", desc: "Updates this meeting and future meetings in the series." },
      { key: "series", label: "Entire series", desc: "Updates every editable meeting in the series." },
    ],
  },
  cancel: {
    title: "Cancel recurring meeting",
    body: "Choose which meetings to cancel.",
    confirm: "Cancel meeting",
    dismiss: "Back",
    options: [
      { key: "occurrence", label: "This occurrence", desc: "Cancels only this meeting." },
      { key: "following", label: "This and following", desc: "Cancels this meeting and future meetings in the series." },
      { key: "series", label: "Entire series", desc: "Cancels every editable meeting in the series." },
    ],
  },
}

const scopeBannerCopy: Record<RecurrenceEditScope, { label: string; helper: string }> = {
  occurrence: { label: "Editing this occurrence", helper: "Changes will not affect the rest of the series." },
  following: { label: "Editing this and following", helper: "Changes will apply from this meeting forward." },
  series: { label: "Editing entire series", helper: "Changes will apply to recurring meetings in this series." },
}

function RecurrenceScopeChooser({
  kind,
  event,
  occurrenceStatus,
  busy = false,
  error,
  onConfirm,
  onDismiss,
}: {
  kind: RecurrenceScopeChooserKind
  event: EventsListItem
  occurrenceStatus?: "normal" | "edited" | "cancelled"
  busy?: boolean
  error?: string
  onConfirm: (scope: RecurrenceEditScope) => void | Promise<void>
  onDismiss: () => void
}) {
  const copy = recurrenceScopeCopy[kind]
  const [scope, setScope] = useState<RecurrenceEditScope>("occurrence")
  const danger = kind === "cancel"
  return (
    <AnimatedDialog
      onClose={onDismiss}
      closeDisabled={busy}
      className="cohort-dialog cohort-dialog--compact-sheet !w-[calc(100vw-32px)] !max-w-md !p-6"
    >
      <DialogHeader>
        <DialogTitle className="text-2xl font-normal">{copy.title}</DialogTitle>
        <DialogDescription>{copy.body} You&apos;re changing “{event.title || "Untitled meeting"}” on {event.date}.</DialogDescription>
      </DialogHeader>
      <div className="space-y-2" role="radiogroup" aria-label={copy.title}>
        {copy.options.map((option) => {
          const selected = scope === option.key
          return (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={busy}
              onClick={() => setScope(option.key)}
              className={`cohort-row-action flex w-full items-start gap-3 rounded-[14px] border bg-white px-4 py-3.5 text-left ${
                selected
                  ? "border-[1.5px] border-[var(--cohort-forest)]"
                  : "border-[var(--cohort-rule)]"
              }`}
            >
              <span className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] ${selected ? "border-[var(--cohort-action)]" : "border-[var(--cohort-radio-idle)]"}`}>
                {selected && <span className="h-[11px] w-[11px] rounded-full bg-[var(--cohort-forest)]" />}
              </span>
              <span>
                <span className="block text-base font-medium text-[var(--cohort-ink)]">{option.label}</span>
                <span className="mt-1 block text-sm leading-5 text-[var(--cohort-ink-muted)]">{option.desc}</span>
              </span>
            </button>
          )
        })}
      </div>
      {kind === "edit" && occurrenceStatus === "edited" && scope === "series" && (
        <p className="rounded-xl bg-[var(--cohort-ochre-soft)] px-3 py-2 text-xs font-medium text-[var(--cohort-ochre)]">
          Individually edited occurrences are preserved unless you reset them.
        </p>
      )}
      {error ? <p role="alert" className="rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] px-3 py-2 text-sm text-[var(--cohort-clay)]">{error}</p> : null}
      <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" size="auto" onClick={onDismiss} disabled={busy} className="cohort-secondary-button w-full sm:w-auto">
          {copy.dismiss}
        </Button>
        <Button
          type="button"
          size="auto"
          onClick={() => void onConfirm(scope)}
          disabled={busy}
          className={`${danger ? "cohort-destructive-secondary-button" : "cohort-primary-button"} w-full sm:w-auto`}
        >
          {busy ? "Updating…" : copy.confirm}
        </Button>
      </div>
    </AnimatedDialog>
  )
}

const INVITE_COHORT = "__COHORT__"
const INVITE_MENTORS = "__MENTORS__"
const INVITE_SCHOLARS = "__SCHOLARS__"

const normalizeInviteeSelection = (
  invitedIds?: Array<string | number>,
  explicitMode?: InviteesMode,
): { mode: InviteeSelectionMode; selected: string[] } => {
  const ids = (invitedIds || []).map(String)
  const modeFromIds: InviteeSelectionMode =
    ids.length === 0
      ? ""
      : ids.includes(INVITE_COHORT)
        ? "cohort"
        : ids.includes(INVITE_MENTORS)
          ? "mentors"
          : ids.includes(INVITE_SCHOLARS)
            ? "scholars"
            : "custom"
  const mode = explicitMode || modeFromIds
  const selected = ids.filter((id) => ![INVITE_COHORT, INVITE_MENTORS, INVITE_SCHOLARS].includes(id))
  return { mode, selected: mode === "custom" ? selected : [] }
}

const coerceInviteesMode = (value?: string | null): InviteesMode | undefined => {
  if (value === "cohort" || value === "mentors" || value === "scholars" || value === "custom") {
    return value
  }
  return undefined
}

const getAttachmentDisplayName = (
  originalName?: string | null,
  keyOrUrl?: string | null,
  fallback?: string,
) => {
  const trimmed = originalName?.trim()
  if (trimmed) return trimmed
  const source = keyOrUrl || ""
  if (source) {
    const segments = source.split(/[/\\]/).filter(Boolean)
    if (segments.length) return segments[segments.length - 1]
  }
  return fallback || "Attachment"
}

const rosterMemberHasRole = (user: RosterMember, roleKey: "mentor" | "mentee") => {
  if (user.roleKey === roleKey) return true
  const role = (user.role || "").toLowerCase()
  return roleKey === "mentor" ? role.includes("mentor") : role.includes("scholar") || role.includes("mentee")
}

const rosterMemberCanDownloadMeetingArtifacts = (user?: RosterMember) => {
  if (!user) return false
  if (user.roleKey === "cohort_admin" || user.roleKey === "cohort_director") return true
  const role = (user.role || "").toLowerCase()
  return role === "cohort admin" || role === "cohort director"
}

const normalizeAttachmentIds = (attachments?: Attachment[]) =>
  (attachments || [])
    .map((att) => att.assetId)
    .filter((id): id is string => typeof id === "string" && id.length > 0)

const pad = (value: number) => value.toString().padStart(2, "0")

const resolveTimeZone = (timeZone?: string | null) => {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  const candidate = timeZone || browserTimeZone
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: candidate }).format()
    return candidate
  } catch {
    return browserTimeZone
  }
}

const getTimeZoneOptions = (...preferredTimeZones: Array<string | null | undefined>) => {
  const supportedValuesOf = (Intl as typeof Intl & {
    supportedValuesOf?: (key: "timeZone") => string[]
  }).supportedValuesOf
  const supportedTimeZones = supportedValuesOf ? supportedValuesOf("timeZone") : []

  return Array.from(
    new Set(
      [...preferredTimeZones, "UTC", ...supportedTimeZones]
        .filter((timeZone): timeZone is string => Boolean(timeZone))
        .map((timeZone) => resolveTimeZone(timeZone)),
    ),
  ).sort((left, right) => left.localeCompare(right))
}

const getDateTimePartsInTimeZone = (value: Date | string, timeZone?: string | null) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: resolveTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(typeof value === "string" ? new Date(value) : value)
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  return {
    year: Number(byType.year),
    month: Number(byType.month),
    day: Number(byType.day),
    hour: Number(byType.hour),
    minute: Number(byType.minute),
    second: Number(byType.second),
  }
}

const toDateInputInTimeZone = (isoString: string, timeZone?: string | null) => {
  const parts = getDateTimePartsInTimeZone(isoString, timeZone)
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

const toTimeInputInTimeZone = (isoString: string, timeZone?: string | null) => {
  const parts = getDateTimePartsInTimeZone(isoString, timeZone)
  return `${pad(parts.hour)}:${pad(parts.minute)}`
}

const fromWallClockInput = (date: string, time: string, timeZone?: string | null, unchangedIso?: string) => {
  // A repeated wall-clock hour identifies two instants. Preserve the original
  // occurrence while the displayed fields are unchanged; edited fields keep
  // the existing resolver policy below.
  if (unchangedIso && Number.isFinite(new Date(unchangedIso).getTime())
    && toDateInputInTimeZone(unchangedIso, timeZone) === date
    && toTimeInputInTimeZone(unchangedIso, timeZone) === time) {
    // Inputs and the local-time API fields have minute precision.
    return new Date(Math.floor(new Date(unchangedIso).getTime() / 60000) * 60000)
  }
  const [year, month, day] = date.split("-").map(Number)
  const [hour, minute] = time.split(":").map(Number)
  const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, 0)
  let result = new Date(desiredAsUtc)

  // Iteratively compare the desired wall clock with how the candidate instant
  // renders in the target zone. Two passes cover normal offsets and DST changes.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const rendered = getDateTimePartsInTimeZone(result, timeZone)
    const renderedAsUtc = Date.UTC(
      rendered.year,
      rendered.month - 1,
      rendered.day,
      rendered.hour,
      rendered.minute,
      rendered.second,
    )
    const adjustment = desiredAsUtc - renderedAsUtc
    if (adjustment === 0) break
    result = new Date(result.getTime() + adjustment)
  }
  return result
}

const REPEATED_TIME_SERIES_ERROR = "Repeating meetings cannot use the later occurrence of a repeated daylight-saving hour. Choose a time outside that hour or save a single meeting."

const isLaterRepeatedInstant = (instant: Date, timeZone: string) => {
  const wallTimestamp = (value: Date) => {
    const parts = getDateTimePartsInTimeZone(value, timeZone)
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
  }
  const timestamp = instant.getTime()
  const wall = wallTimestamp(instant)
  // Adjacent-day offsets cover both sides, including half-hour changes. Only
  // a real earlier instant with the same wall time proves a later occurrence.
  return [-1, 1].some((day) => {
    const sample = timestamp + day * 24 * 60 * 60 * 1000
    const candidate = wall - (wallTimestamp(new Date(sample)) - sample)
    return candidate < timestamp && wallTimestamp(new Date(candidate)) === wall
  })
}

const isValidWallClockInput = (date: string, time: string, timeZone?: string | null) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return false
  const [year, month, day] = date.split("-").map(Number)
  const [hour, minute] = time.split(":").map(Number)
  const converted = fromWallClockInput(date, time, timeZone)
  const rendered = getDateTimePartsInTimeZone(converted, timeZone)
  return rendered.year === year &&
    rendered.month === month &&
    rendered.day === day &&
    rendered.hour === hour &&
    rendered.minute === minute
}

const timeStringToMinutes = (value: string) => {
  const [h, m] = value.split(":").map(Number)
  return h * 60 + m
}


const normalizeGuestName = (value: string) => value.replace(/\s+/g, " ").trim().slice(0, 80)

const getAttendanceEntryId = (entry: MeetingAttendanceEntry) => {
  if (entry.attendee_id) return entry.attendee_id
  if (entry.attendee_type === "guest" || entry.user_id === null || entry.user_id === undefined) {
    return `guest:${normalizeGuestName(entry.display_name || "Guest").toLocaleLowerCase()}`
  }
  return `user:${entry.user_id}`
}

const getAttendanceEntryDisplayName = (entry: MeetingAttendanceEntry, rosterById: Map<string, RosterMember>) => {
  if (entry.attendee_type === "guest" || entry.user_id === null || entry.user_id === undefined) {
    return entry.display_name || "Guest"
  }
  const member = rosterById.get(String(entry.user_id))
  return entry.display_name || member?.name || `User ${entry.user_id}`
}

const getAttendanceEntryRole = (entry: MeetingAttendanceEntry, rosterById: Map<string, RosterMember>) => {
  if (entry.attendee_type === "guest" || entry.user_id === null || entry.user_id === undefined) {
    return entry.role || "Guest"
  }
  const member = rosterById.get(String(entry.user_id))
  return entry.role || member?.role || (entry.is_host ? "Host" : "Invitee")
}

const getAttendanceRosterMember = (entry: MeetingAttendanceEntry, rosterById: Map<string, RosterMember>) => {
  if (entry.user_id === null || entry.user_id === undefined) return undefined
  return rosterById.get(String(entry.user_id))
}

const toAbsoluteUrl = (url: string) => {
  if (/^https?:\/\//i.test(url)) return url
  if (typeof window === "undefined" || !window.location?.origin) return url
  return new URL(url, window.location.origin).toString()
}

const copyTextToClipboard = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "")
  textarea.style.position = "fixed"
  textarea.style.top = "-1000px"
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand("copy")
  document.body.removeChild(textarea)
  if (!copied) {
    throw new Error("Unable to copy")
  }
}

const SOON_THRESHOLD_MS = 30 * 60 * 1000

const getPopupState = (event: EventsListItem): PopupState => {
  const now = Date.now()
  const start = event.startTimeIso ? new Date(event.startTimeIso).getTime() : 0
  const end = event.endTimeIso ? new Date(event.endTimeIso).getTime() : 0
  if (event.canJoinVideo && end && end <= now) return "rejoin"
  if (end && end <= now) return "past"
  if (start && start <= now) return "live"
  if (start && start - now <= SOON_THRESHOLD_MS) return "soon"
  return "upcoming"
}

const getSecondsToStart = (event: EventsListItem) => {
  if (!event.startTimeIso) return 0
  return Math.max(0, Math.floor((new Date(event.startTimeIso).getTime() - Date.now()) / 1000))
}

const formatCountdown = (seconds: number) => {
  const minutes = Math.floor(Math.max(0, seconds) / 60)
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  const secs = Math.max(0, seconds) % 60
  return hours > 0 ? `${pad(hours)}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`
}

const formatDateParts = (iso?: string, timeZone?: string) => {
  const date = iso ? new Date(iso) : new Date()
  return {
    weekday: date.toLocaleDateString("en-US", { timeZone, weekday: "short" }),
    day: date.toLocaleDateString("en-US", { timeZone, day: "numeric" }),
    month: date.toLocaleDateString("en-US", { timeZone, month: "short" }),
    longDate: date.toLocaleDateString("en-US", { timeZone, weekday: "short", month: "short", day: "numeric" }),
  }
}

const formatShortDate = (value: string) => {
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const date = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(value)
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const formatWallClockTimeLabel = (value?: string) => {
  if (!value) return ""
  const [hours, minutes] = value.split(":").map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value
  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })
}

const getInitials = (name?: string | null) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

const avatarColors = [
  "bg-foundation-orange-500",
  "bg-foundation-base-darkbase-dark-500",
  "bg-foundation-base-darkbase-dark-300",
  "bg-foundation-orange-300",
  "bg-foundation-calm-blue-300",
]

const getStableAvatarColor = (seed?: string | number | null) => {
  const text = String(seed || "")
  const total = Array.from(text).reduce((sum, char) => sum + char.charCodeAt(0), 0)
  return avatarColors[total % avatarColors.length]
}

const getRosterAvatarUrl = (member?: RosterMember | null) =>
  member?.avatarUrl || member?.avatar_url || member?.profile?.avatar_url || null

function AvatarBubble({
  name,
  seed,
  size = "md",
  muted = false,
  avatarUrl,
}: {
  name?: string | null
  seed?: string | number | null
  size?: "sm" | "md" | "lg"
  muted?: boolean
  avatarUrl?: string | null
}) {
  const [imageAvailable, setImageAvailable] = useState(Boolean(avatarUrl))
  useEffect(() => {
    setImageAvailable(Boolean(avatarUrl))
  }, [avatarUrl])

  const sizeClass = size === "lg" ? "h-9 w-9 text-[13px]" : size === "sm" ? "h-7 w-7 text-[11px]" : "h-8 w-8 text-xs"
  const shouldShowAvatar = Boolean(avatarUrl && imageAvailable)
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white font-semibold text-white ${sizeClass} ${getStableAvatarColor(seed || name)} ${muted ? "opacity-45" : ""}`}
      title={name || undefined}
    >
      {shouldShowAvatar ? (
        <img
          src={avatarUrl || undefined}
          alt={name ? `${name} avatar` : "Profile avatar"}
          className="h-full w-full object-cover"
          onError={() => setImageAvailable(false)}
        />
      ) : (
        getInitials(name)
      )}
    </span>
  )
}

const popupShellClass =
  "!flex !w-[calc(100vw-32px)] !max-w-[640px] !flex-col sm:!w-[640px] max-h-[calc(100dvh-32px)] !overflow-hidden rounded-[24px] bg-white !p-0 sm:!p-0 gap-0 shadow-[0_20px_60px_-10px_rgba(15,18,30,0.30),0_4px_12px_rgba(15,18,30,0.10)]"

const popupHeroClass =
  "cohort-meeting-details__hero relative shrink-0 overflow-hidden bg-[var(--cohort-hero)] text-[var(--cohort-hero-ink)]"

const popupCardClass =
  "rounded-[16px] border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] p-4"

const popupCapClass =
  "text-[11px] font-semibold uppercase leading-none tracking-[0.08em] text-[var(--cohort-ink-muted)]"

const popupPillButtonClass =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold"

const eventEditorHeroClass =
  "relative shrink-0 border-b border-[var(--cohort-rule)] bg-[var(--cohort-paper)]"

const eventEditorSectionClass =
  "py-4"

const eventEditorButtonClass =
  "inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-2 py-2 text-[13px] font-semibold shadow-none sm:px-4 sm:text-sm"

const formatQuestionSubmittedAt = (iso?: string | null) => {
  if (!iso) return ""
  const submittedAt = new Date(iso).getTime()
  if (Number.isNaN(submittedAt)) return ""
  const daysAgo = Math.floor((Date.now() - submittedAt) / (24 * 60 * 60 * 1000))
  if (daysAgo <= 0) return "Today"
  if (daysAgo === 1) return "Yesterday"
  if (daysAgo < 7) return `${daysAgo} days ago`
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const getQuestionStatusLabel = (status: MeetingQuestionSubmissionStatus) => {
  if (status === "submitted") return "New"
  return status.charAt(0).toUpperCase() + status.slice(1)
}

const getQuestionStatusPillClass = (status: MeetingQuestionSubmissionStatus) => {
  if (status === "answered") return "bg-[var(--cohort-success-soft)] text-[var(--cohort-success)]"
  if (status === "archived") return "bg-[var(--cohort-secondary-fill)] text-[var(--cohort-ink-muted)]"
  if (status === "reviewed") return "bg-[rgba(89,96,119,0.12)] text-[var(--cohort-sage)]"
  return "bg-[var(--cohort-ochre-soft)] text-[var(--cohort-ochre)]"
}

const questionStatusSortOrder: Record<MeetingQuestionSubmissionStatus, number> = {
  submitted: 0,
  reviewed: 1,
  answered: 2,
  archived: 3,
}

const getQuestionTimestamp = (submission: MeetingQuestionSubmission) => {
  const time = new Date(submission.created_at || submission.updated_at || 0).getTime()
  return Number.isNaN(time) ? 0 : time
}

const sortQuestionSubmissionsByStatus = (submissions: MeetingQuestionSubmission[]) =>
  [...submissions].sort((left, right) => {
    const statusDelta = questionStatusSortOrder[left.status] - questionStatusSortOrder[right.status]
    if (statusDelta !== 0) return statusDelta
    return getQuestionTimestamp(right) - getQuestionTimestamp(left)
  })

type HostNoteSaveStatus = "idle" | "saving" | "saved" | "error"

function DateStamp({ iso, timeZone }: { iso?: string; timeZone?: string }) {
  const parts = formatDateParts(iso, timeZone)
  return (
    <div className="cohort-meeting-details__date flex w-[72px] shrink-0 flex-col border-r border-white/20 pr-4 text-left sm:w-[82px] sm:pr-5">
      <span className="text-[11px] font-semibold uppercase leading-none tracking-[0.1em] text-[var(--cohort-hero-eyebrow)]">
        {parts.weekday}
      </span>
      <span className="mt-1 font-headings-h2 text-[38px] font-normal leading-none tracking-[-0.04em] text-[var(--cohort-hero-ink)]">
        {parts.day}
      </span>
      <span className="mt-1 text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-[var(--cohort-hero-muted)]">
        {parts.month}
      </span>
    </div>
  )
}

function MeetingQuestionsPanel({
  meetingId,
  collectionEnabled,
  canReview,
  submissionCount = 0,
  unreviewedCount = 0,
  onCountsUpdated,
  past = false,
}: {
  meetingId: string
  collectionEnabled: boolean
  canReview: boolean
  submissionCount?: number
  unreviewedCount?: number
  onCountsUpdated?: (meetingId: string, totalCount: number, unreviewedCount: number) => void
  past?: boolean
}) {
  const [questionSubmissions, setQuestionSubmissions] = useState<MeetingQuestionSubmission[]>([])
  const [questionsLoading, setQuestionsLoading] = useState(false)
  const [questionError, setQuestionError] = useState("")
  const [questionBody, setQuestionBody] = useState("")
  const [questionSaving, setQuestionSaving] = useState(false)
  const [questionUpdatingId, setQuestionUpdatingId] = useState<string | null>(null)
  const [hostNoteDrafts, setHostNoteDrafts] = useState<Record<string, string>>({})
  const [hostNoteSaveStatuses, setHostNoteSaveStatuses] = useState<Record<string, HostNoteSaveStatus>>({})
  const [sent, setSent] = useState(false)
  const [reviewCollapsed, setReviewCollapsed] = useState(canReview)
  const [questionsLoaded, setQuestionsLoaded] = useState(false)
  const onCountsUpdatedRef = useRef(onCountsUpdated)
  const questionSubmissionsRef = useRef<MeetingQuestionSubmission[]>([])
  const hostNoteDraftsRef = useRef<Record<string, string>>({})
  const hostNoteSaveTimersRef = useRef<Record<string, number>>({})
  const hostNoteRequestIdsRef = useRef<Record<string, number>>({})
  const hostNoteRequestSeqRef = useRef(0)
  const shouldShowQuestions = collectionEnabled || (canReview && submissionCount > 0)
  const allowSubmit = collectionEnabled && !past && !canReview
  const contentId = `meeting-questions-${meetingId}`

  useEffect(() => {
    onCountsUpdatedRef.current = onCountsUpdated
  }, [onCountsUpdated])

  useEffect(() => {
    setReviewCollapsed(canReview)
    setQuestionsLoaded(false)
  }, [canReview, meetingId])

  useEffect(() => {
    return () => {
      Object.values(hostNoteSaveTimersRef.current).forEach((timerId) => window.clearTimeout(timerId))
      hostNoteSaveTimersRef.current = {}
    }
  }, [])

  const clearHostNoteSaveTimer = useCallback((submissionId: string) => {
    const existingTimer = hostNoteSaveTimersRef.current[submissionId]
    if (existingTimer) {
      window.clearTimeout(existingTimer)
      delete hostNoteSaveTimersRef.current[submissionId]
    }
  }, [])

  const setHostNoteSaveStatus = useCallback((submissionId: string, status: HostNoteSaveStatus) => {
    setHostNoteSaveStatuses((prev) => {
      if (status === "idle") {
        if (!prev[submissionId]) return prev
        const next = { ...prev }
        delete next[submissionId]
        return next
      }
      return { ...prev, [submissionId]: status }
    })
  }, [])

  const getSavedHostNote = useCallback((submissionId: string) => {
    return questionSubmissionsRef.current.find((submission) => submission.id === submissionId)?.host_note ?? ""
  }, [])

  const syncQuestionState = useCallback(
    (submissions: MeetingQuestionSubmission[]) => {
      const sortedSubmissions = sortQuestionSubmissionsByStatus(submissions)
      questionSubmissionsRef.current = sortedSubmissions
      setQuestionSubmissions(sortedSubmissions)
      setHostNoteDrafts((prev) => {
        const next: Record<string, string> = {}
        sortedSubmissions.forEach((submission) => {
          next[submission.id] = prev[submission.id] ?? submission.host_note ?? ""
        })
        hostNoteDraftsRef.current = next
        return next
      })
      setHostNoteSaveStatuses((prev) => {
        const next: Record<string, HostNoteSaveStatus> = {}
        sortedSubmissions.forEach((submission) => {
          if (prev[submission.id]) next[submission.id] = prev[submission.id]
        })
        return next
      })
      onCountsUpdatedRef.current?.(
        meetingId,
        sortedSubmissions.length,
        sortedSubmissions.filter((submission) => submission.status === "submitted").length,
      )
    },
    [meetingId],
  )

  const loadQuestions = useCallback(async () => {
    if (!meetingId || !shouldShowQuestions || !canReview || reviewCollapsed || questionsLoaded) return
    setQuestionsLoading(true)
    setQuestionError("")
    try {
      const data = await meetings.getMeetingQuestions(meetingId)
      syncQuestionState(Array.isArray(data) ? data : [])
    } catch (err: any) {
      setQuestionError(err?.response?.data?.detail || err?.message || "Unable to load questions and comments.")
      syncQuestionState([])
    } finally {
      setQuestionsLoaded(true)
      setQuestionsLoading(false)
    }
  }, [canReview, meetingId, questionsLoaded, reviewCollapsed, shouldShowQuestions, syncQuestionState])

  useEffect(() => {
    loadQuestions()
  }, [loadQuestions])

  const handleSubmitQuestion = async () => {
    if (!meetingId || !questionBody.trim()) return
    setQuestionSaving(true)
    setQuestionError("")
    try {
      const saved = await meetings.submitMeetingQuestion(meetingId, {
        body: questionBody.trim(),
      })
      if (canReview) {
        syncQuestionState([...questionSubmissions, saved])
      } else {
        onCountsUpdatedRef.current?.(meetingId, submissionCount + 1, unreviewedCount + 1)
      }
      setQuestionBody("")
      setSent(true)
    } catch (err: any) {
      setQuestionError(err?.response?.data?.detail || err?.message || "Unable to submit your question or comment.")
    } finally {
      setQuestionSaving(false)
    }
  }

  const handleUpdateQuestion = async (
    submission: MeetingQuestionSubmission,
    updates: Partial<{ status: MeetingQuestionSubmissionStatus; host_note: string }>,
  ) => {
    if (!meetingId) return
    setQuestionUpdatingId(submission.id)
    setQuestionError("")
    try {
      const saved = await meetings.updateMeetingQuestion(meetingId, submission.id, updates)
      syncQuestionState(questionSubmissionsRef.current.map((item) => (item.id === saved.id ? saved : item)))
    } catch (err: any) {
      setQuestionError(err?.response?.data?.detail || err?.message || "Unable to update this question or comment.")
    } finally {
      setQuestionUpdatingId(null)
    }
  }

  const saveHostNote = useCallback(
    async (submissionId: string, note: string) => {
      if (!meetingId) return
      const savedNote = getSavedHostNote(submissionId)
      if (note === savedNote) {
        setHostNoteSaveStatus(submissionId, "idle")
        return
      }

      const requestId = hostNoteRequestSeqRef.current + 1
      hostNoteRequestSeqRef.current = requestId
      hostNoteRequestIdsRef.current[submissionId] = requestId
      setHostNoteSaveStatus(submissionId, "saving")

      try {
        const saved = await meetings.updateMeetingQuestion(meetingId, submissionId, { host_note: note })
        if (hostNoteRequestIdsRef.current[submissionId] !== requestId) return

        syncQuestionState(
          questionSubmissionsRef.current.map((item) =>
            item.id === saved.id
              ? {
                  ...item,
                  host_note: saved.host_note,
                  updated_at: saved.updated_at,
                }
              : item,
          ),
        )
        const currentDraft = hostNoteDraftsRef.current[submissionId] ?? ""
        setHostNoteSaveStatus(submissionId, currentDraft === (saved.host_note ?? "") ? "saved" : "idle")
      } catch {
        if (hostNoteRequestIdsRef.current[submissionId] === requestId) {
          setHostNoteSaveStatus(submissionId, "error")
        }
      }
    },
    [getSavedHostNote, meetingId, setHostNoteSaveStatus, syncQuestionState],
  )

  const scheduleHostNoteAutosave = useCallback(
    (submissionId: string, note: string) => {
      clearHostNoteSaveTimer(submissionId)
      if (note === getSavedHostNote(submissionId)) {
        setHostNoteSaveStatus(submissionId, "idle")
        return
      }
      hostNoteSaveTimersRef.current[submissionId] = window.setTimeout(() => {
        delete hostNoteSaveTimersRef.current[submissionId]
        saveHostNote(submissionId, hostNoteDraftsRef.current[submissionId] ?? "")
      }, 800)
    },
    [clearHostNoteSaveTimer, getSavedHostNote, saveHostNote, setHostNoteSaveStatus],
  )

  const handleHostNoteChange = useCallback(
    (submissionId: string, value: string) => {
      hostNoteDraftsRef.current = {
        ...hostNoteDraftsRef.current,
        [submissionId]: value,
      }
      setHostNoteDrafts(hostNoteDraftsRef.current)
      setHostNoteSaveStatus(submissionId, "idle")
      scheduleHostNoteAutosave(submissionId, value)
    },
    [scheduleHostNoteAutosave, setHostNoteSaveStatus],
  )

  const handleHostNoteBlur = useCallback(
    (submissionId: string) => {
      clearHostNoteSaveTimer(submissionId)
      saveHostNote(submissionId, hostNoteDraftsRef.current[submissionId] ?? "")
    },
    [clearHostNoteSaveTimer, saveHostNote],
  )

  if (!shouldShowQuestions) return null

  return (
    <div className={`${popupCardClass} space-y-3`}>
      {canReview ? (
        <button
          type="button"
          aria-expanded={!reviewCollapsed}
          aria-controls={contentId}
          onClick={() => setReviewCollapsed((value) => !value)}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <span className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[var(--cohort-forest)]" />
            <span className={popupCapClass}>Questions members brought</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[var(--cohort-wash)] px-2 text-xs font-semibold text-[var(--cohort-ink-muted)]">
              {questionSubmissions.length || submissionCount}
            </span>
            <ChevronDown
              className={`h-4 w-4 text-[var(--cohort-ink-muted)] transition-transform ${reviewCollapsed ? "" : "rotate-180"}`}
              aria-hidden="true"
            />
          </span>
        </button>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[var(--cohort-forest)]" />
            <p className={popupCapClass}>Questions & Comments</p>
          </div>
        </div>
      )}

      {(!canReview || !reviewCollapsed) && (
        <div id={contentId} className="space-y-3">
          {allowSubmit && sent ? (
            <div className="flex flex-col gap-3 rounded-xl bg-[var(--cohort-success-soft)] p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 shrink-0 text-[var(--cohort-success)]" />
                <div>
                  <p className="text-sm font-medium text-[var(--cohort-ink)]">
                    Submitted
                  </p>
                  <p className="mt-0.5 text-xs text-[var(--cohort-ink-muted)]">
                    The host may review or discuss it during the meeting.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="auto"
                variant="ghost"
                onClick={() => {
                  setQuestionError("")
                  setSent(false)
                }}
                className="cohort-secondary-button h-auto !min-h-9 self-start px-3 py-1.5 text-xs font-semibold sm:self-center"
              >
                <Plus className="h-3.5 w-3.5" />
                Ask another
              </Button>
            </div>
          ) : allowSubmit ? (
            <div className="space-y-2">
              <p className="text-sm text-[var(--cohort-ink-muted)]">
                Share a question or comment for the host to review. It may be discussed during the meeting.
              </p>
              <div className="flex gap-2">
                <Input
                  value={questionBody}
                  onChange={(e) => setQuestionBody(e.target.value)}
                  placeholder="Type a question or comment..."
                  className="rounded-xl bg-white"
                />
                <Button
                  size="auto"
                  onClick={handleSubmitQuestion}
                  disabled={questionSaving || !questionBody.trim()}
                  className={`${popupPillButtonClass} cohort-primary-button px-4`}
                >
                  <Send className="h-4 w-4" />
                  {questionSaving ? "Sending..." : "Send"}
                </Button>
              </div>
            </div>
          ) : null}

          {canReview && questionsLoading ? (
            <p className="text-sm text-[var(--cohort-ink-muted)]">Loading questions and comments...</p>
          ) : canReview && questionSubmissions.length === 0 ? (
            <p className="text-sm text-[var(--cohort-ink-muted)]">
              No questions or comments yet.
            </p>
          ) : canReview && questionSubmissions.length > 0 ? (
            <div className="flex flex-col gap-2">
              {questionSubmissions.map((submission) => {
                const isUpdating = questionUpdatingId === submission.id
                const hostNoteSaveStatus = hostNoteSaveStatuses[submission.id] || "idle"
                return (
                  <div key={submission.id} className="flex flex-col gap-2 rounded-[14px] border border-[var(--cohort-rule)] bg-white px-3.5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <AvatarBubble
                          name={submission.author?.name || submission.author?.email}
                          seed={submission.author?.id || submission.author?.email}
                          size="sm"
                          avatarUrl={submission.author?.avatar_url}
                        />
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-medium leading-tight text-[var(--cohort-ink)]">
                            {canReview ? submission.author?.name || submission.author?.email || "Unknown" : "You"}
                          </p>
                          <p className="mt-0.5 text-[11px] leading-tight text-[var(--cohort-ink-muted)]">
                            {formatQuestionSubmittedAt(submission.created_at)}
                          </p>
                        </div>
                      </div>
                      {canReview ? (
                        <select
                          aria-label={`Status for ${submission.author?.name || submission.author?.email || "question"}`}
                          value={submission.status}
                          disabled={isUpdating}
                          onChange={(e) =>
                            handleUpdateQuestion(submission, {
                              status: e.target.value as MeetingQuestionSubmissionStatus,
                            })
                          }
                          className={`ml-auto h-6 rounded-full border-0 px-2 text-[11px] font-semibold leading-none tracking-[0.04em] ${getQuestionStatusPillClass(submission.status)}`}
                        >
                          <option value="submitted">New</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="answered">Answered</option>
                          <option value="archived">Archived</option>
                        </select>
                      ) : (
                        <span className={`ml-auto rounded-full px-2 py-1 text-[11px] font-semibold leading-none tracking-[0.04em] ${getQuestionStatusPillClass(submission.status)}`}>
                          {getQuestionStatusLabel(submission.status)}
                        </span>
                      )}
                    </div>
                    <p className="m-0 whitespace-pre-line text-sm leading-[1.5] text-[var(--cohort-ink-muted)]">{submission.body}</p>
                    {canReview && (
                      <div className="rounded-[10px] bg-[var(--cohort-wash)] px-2.5 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className={popupCapClass}>Note from host</span>
                          <div className="flex min-h-6 items-center gap-2 text-xs font-medium">
                            {hostNoteSaveStatus === "saving" && (
                              <span className="text-[var(--cohort-ink-muted)]">Saving...</span>
                            )}
                            {hostNoteSaveStatus === "saved" && (
                              <span className="text-[var(--cohort-success)]">Saved</span>
                            )}
                            {hostNoteSaveStatus === "error" && (
                              <>
                                <span className="text-[var(--cohort-clay)]">Could not save</span>
                                <Button
                                  type="button"
                                  size="auto"
                                  variant="ghost"
                                  onClick={() => saveHostNote(submission.id, hostNoteDraftsRef.current[submission.id] ?? "")}
                                  className="h-auto min-h-0 rounded-full px-2 py-1 text-xs font-semibold text-[var(--cohort-clay)] hover:bg-[var(--cohort-clay-soft)]"
                                >
                                  Retry
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <Textarea
                          value={hostNoteDrafts[submission.id] ?? ""}
                          onChange={(e) => handleHostNoteChange(submission.id, e.target.value)}
                          onBlur={() => handleHostNoteBlur(submission.id)}
                          placeholder="Add a host note..."
                          className="mt-1 min-h-[52px] resize-none rounded-lg border-0 bg-transparent p-0 text-[13px] leading-[1.4] text-[var(--cohort-ink)] shadow-none focus-visible:ring-0"
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : null}
          {questionError && <p className="text-xs text-[var(--cohort-clay)]">{questionError}</p>}
        </div>
      )}
    </div>
  )
}

function MeetingInspireHistoryPanel({ meetingId }: { meetingId: string }) {
  const [activities, setActivities] = useState<MeetingInspireActivity[]>([])
  const [selectedId, setSelectedId] = useState("")
  const [responses, setResponses] = useState<MeetingInspireResponse[]>([])
  const [nextPage, setNextPage] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const load = useCallback(async (activityId?: string, page = 1, append = false) => {
    try {
      if (!append) setLoading(true)
      const payload = await meetings.getMeetingInspireHistory(meetingId, {
        activity_id: activityId || undefined,
        page,
      })
      setActivities(payload.activities)
      setSelectedId(payload.selected_activity_id || "")
      setResponses((current) => append ? [...current, ...payload.responses.results] : payload.responses.results)
      setNextPage(payload.responses.next_page || null)
      setError("")
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Unable to load Inspire responses.")
    } finally {
      setLoading(false)
    }
  }, [meetingId])

  useEffect(() => { void load() }, [load])

  if (!loading && activities.length === 0 && !error) return null
  const selected = activities.find((activity) => activity.id === selectedId) || activities[0]

  return (
    <div className={`${popupCardClass} space-y-3`} data-testid="meeting-inspire-history">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-foundation-orange-500" /><span className={popupCapClass}>Inspire Responses</span></span>
        {activities.length > 1 && (
          <select value={selectedId} onChange={(event) => void load(event.target.value)} className="max-w-[16rem] rounded-full border border-foundation-base-darkbase-dark-500/10 bg-white px-3 py-1.5 text-xs">
            {activities.map((activity) => <option key={activity.id} value={activity.id}>{activity.card_prompt_snapshot}</option>)}
          </select>
        )}
      </div>
      {loading ? <p className="text-sm text-foundation-base-darkbase-dark-300">Loading Inspire responses…</p> : error ? <p className="text-xs text-red-600">{error}</p> : selected ? (
        <>
          <div className="rounded-xl bg-foundation-orange-50/60 p-3">
            <p className="font-medium text-foundation-base-darkbase-dark-700">{selected.card_prompt_snapshot}</p>
            <p className="mt-1 text-xs text-foundation-base-darkbase-dark-300">{selected.visibility_mode === "anonymous" ? "Anonymous to presenters" : "Named responses"} · {selected.response_count} responses</p>
          </div>
          {responses.length === 0 ? <p className="text-sm text-foundation-base-darkbase-dark-300">No responses were submitted.</p> : (
            <div className="space-y-2">
              {responses.map((response) => <article key={response.id} className="rounded-xl border border-foundation-base-darkbase-dark-500/10 bg-white p-3"><div className="flex items-center justify-between gap-3"><strong className="text-sm text-foundation-base-darkbase-dark-700">{response.display_name}</strong><time className="text-xs text-foundation-base-darkbase-dark-300">{new Date(response.updated_at).toLocaleString()}</time></div><p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-foundation-base-darkbase-dark-500">{response.body}</p></article>)}
            </div>
          )}
          {nextPage && <Button type="button" variant="secondary" size="auto" onClick={() => void load(selected.id, nextPage, true)} className="w-full">Load more responses</Button>}
        </>
      ) : null}
    </div>
  )
}

type EventsListCardProps = {
  title?: string
  events: EventsListItem[]
  loading?: boolean
  error?: string | null
  showPast: boolean
  onTogglePast: (checked: boolean) => void
  showRequiresAttendance?: boolean
  onToggleRequiresAttendance?: (checked: boolean) => void
  onSelectEvent?: (event: EventsListItem) => void
  canCreateEvent?: boolean
  requiresAttendanceLabel?: string
  loadingLabel?: string
  emptyLabel?: string
  isAdmin?: boolean
  currentUserId?: string
  cohortId?: string | null
  roster?: RosterMember[]
  roleLabels?: CohortRoleLabelSource
  deepLinkedMeetingId?: string | null
  buildMeetingDetailsUrl?: (meetingId: string) => string
  onMeetingPopupUrlChange?: (meetingId: string | null) => void
  onEventsChange?: (events: EventsListItem[]) => void
  className?: string
  continuityRelationships?: MentorshipRelationship[]
  onOpenContinuity?: (relationshipId: string) => void
}


export type EventsListCardHandle = {
  openCreateEvent: (defaults?: CreateEventDefaults) => void
  openManageEvent: (eventId: string) => void
  openEventDetails: (eventId: string) => void
}

export type CreateEventDefaults = {
  title?: string
  inviteesMode?: InviteesMode
  invitedUserIds?: Array<string | number>
}

function EventDetailsDialog({
  viewerTimeZone,
  event,
  onClose,
  onJoinMeeting,
  isAdmin,
  currentUserId,
  roster,
  roleLabels,
  onEndMeeting,
  onAttendanceUpdated,
  onQuestionCountsUpdated,
  onEdit,
  onRestoreOccurrence,
  buildMeetingDetailsUrl,
  continuityRelationships = [],
  onOpenContinuity,
}: {
  viewerTimeZone?: string
  event: EventsListItem | null
  onClose: () => void
  onJoinMeeting: (event: EventsListItem) => void
  isAdmin: boolean
  currentUserId?: string
  roster: RosterMember[]
  roleLabels?: CohortRoleLabelSource
  onEndMeeting?: (meetingId: string) => Promise<void>
  onAttendanceUpdated?: (meetingId: string, attendanceTracked: boolean) => void
  onQuestionCountsUpdated?: (meetingId: string, totalCount: number, unreviewedCount: number) => void
  onEdit?: (event: EventsListItem) => void
  onRestoreOccurrence?: (event: EventsListItem) => Promise<void>
  buildMeetingDetailsUrl?: (meetingId: string) => string
  continuityRelationships?: MentorshipRelationship[]
  onOpenContinuity?: (relationshipId: string) => void
}) {
  const eventTimeZone = resolveMeetingTimeZone(event?.timeZone, viewerTimeZone)
  const [attachmentError, setAttachmentError] = useState<string>("")
  const [attendanceEntries, setAttendanceEntries] = useState<MeetingAttendanceEntry[]>([])
  const [attendanceChecked, setAttendanceChecked] = useState<Set<string>>(new Set())
  const [manualGuestNames, setManualGuestNames] = useState<string[]>([])
  const [manualGuestNameInput, setManualGuestNameInput] = useState("")
  const [attendanceLoading, setAttendanceLoading] = useState(false)
  const [attendanceSaving, setAttendanceSaving] = useState(false)
  const [attendanceError, setAttendanceError] = useState("")
  const [endingMeeting, setEndingMeeting] = useState(false)
  const [endMeetingError, setEndMeetingError] = useState("")
  const [transcriptError, setTranscriptError] = useState("")
  const [transcriptDownloading, setTranscriptDownloading] = useState(false)
  const [recordingError, setRecordingError] = useState("")
  const [recordingDownloading, setRecordingDownloading] = useState(false)
  const [restoreError, setRestoreError] = useState("")
  const [restoringOccurrence, setRestoringOccurrence] = useState(false)
  const [meetingLinkCopyStatus, setMeetingLinkCopyStatus] = useState<"idle" | "copied" | "error">("idle")
  const [detailsLinkCopyStatus, setDetailsLinkCopyStatus] = useState<"idle" | "copied" | "error">("idle")
  const [popupState, setPopupState] = useState<PopupState>(event ? getPopupState(event) : "upcoming")
  const [secondsToStart, setSecondsToStart] = useState(event ? getSecondsToStart(event) : 0)
  const [attendanceReviewOpen, setAttendanceReviewOpen] = useState(false)
  const eventCreatedById = event?.createdById
  const adminIsNotInvited = Boolean(
    event && isAdmin && currentUserId !== undefined
    && !isCurrentUserMeetingParticipant(event, currentUserId),
  )
  const meetingEnded = event?.endTimeIso ? new Date(event.endTimeIso).getTime() <= Date.now() : event?.status === "past"
  const meetingStarted = event?.startTimeIso ? new Date(event.startTimeIso).getTime() <= Date.now() : false
  const canManageAttendance =
    Boolean(event) && meetingEnded && (isAdmin || (currentUserId && currentUserId === eventCreatedById))
  const canEndMeeting =
    Boolean(event) &&
    !meetingEnded &&
    (isAdmin || (currentUserId && currentUserId === eventCreatedById)) &&
    meetingStarted

  const rosterById = useMemo(() => {
    const map = new Map<string, RosterMember>()
    roster.forEach((member) => {
      map.set(String(member.id), member)
    })
    return map
  }, [roster])

  const currentMember = currentUserId ? rosterById.get(String(currentUserId)) : undefined
  const canDownloadTranscript =
    Boolean(event) &&
    (isAdmin || rosterMemberCanDownloadMeetingArtifacts(currentMember))
  const canDownloadRecording = canDownloadTranscript
  const canReviewQuestions = Boolean(event) && (event?.canReviewQuestions || isAdmin || (currentUserId && currentUserId === eventCreatedById))

  useEffect(() => {
    if (!event) return
    const syncState = () => {
      setPopupState(getPopupState(event))
      setSecondsToStart(getSecondsToStart(event))
    }
    syncState()
    const stateTimer = window.setInterval(syncState, 60 * 1000)
    const countdownTimer = window.setInterval(() => setSecondsToStart(getSecondsToStart(event)), 1000)
    return () => {
      window.clearInterval(stateTimer)
      window.clearInterval(countdownTimer)
    }
  }, [event])

  const loadAttendance = useCallback(async () => {
    if (!event?.id || !canManageAttendance) return
    setAttendanceLoading(true)
    setAttendanceError("")
    try {
      const data = await meetings.getMeetingAttendance(event.id)
      const entries = Array.isArray(data.attendees) ? data.attendees : []
      setAttendanceEntries(entries)
      const checked = new Set(entries.filter((entry) => entry.attended).map((entry) => getAttendanceEntryId(entry)))
      setAttendanceChecked(checked)
      setManualGuestNames(
        entries
          .filter((entry) => entry.attendee_type === "guest" && !entry.locked)
          .map((entry) => normalizeGuestName(entry.display_name || "Guest"))
          .filter(Boolean),
      )
      setManualGuestNameInput("")
    } catch (err: any) {
      setAttendanceError(err?.response?.data?.detail || err?.message || "Unable to load attendance.")
      setAttendanceEntries([])
      setAttendanceChecked(new Set())
      setManualGuestNames([])
      setManualGuestNameInput("")
    } finally {
      setAttendanceLoading(false)
    }
  }, [canManageAttendance, event?.id])

  useEffect(() => {
    if (canManageAttendance) {
      loadAttendance()
    }
  }, [canManageAttendance, loadAttendance])

  useEffect(() => {
    setMeetingLinkCopyStatus("idle")
    setDetailsLinkCopyStatus("idle")
  }, [event?.id])

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-US", { timeZone: eventTimeZone, hour: "numeric", minute: "2-digit" })

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      timeZone: eventTimeZone,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })

  const formatRecurrence = (recurrence?: RecurrenceRule | null) => {
    if (!recurrence) return ""
    const interval = Math.max(1, recurrence.interval || 1)
    const periodLabel = recurrence.period === "day" ? "day" : recurrence.period === "week" ? "week" : "month"
    const everyLabel = interval === 1 ? periodLabel : `${interval} ${periodLabel}${interval > 1 ? "s" : ""}`
    const days =
      recurrence.period !== "day" && recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0
        ? ` on ${recurrence.daysOfWeek
            .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
            .join(", ")}`
        : ""
    const until = recurrence.endDate ? ` until ${formatShortDate(recurrence.endDate)}` : ""
    return `Repeats every ${everyLabel}${days}${until}`
  }

  const resolveAttachmentLink = async (
    attachment: Attachment,
  ): Promise<{ url: string; download: boolean; newTab: boolean } | null> => {
    const directUrl = attachment.url
    if (directUrl && /^https?:\/\//i.test(directUrl)) {
      return { url: directUrl, download: false, newTab: true }
    }

    const key = attachment.key || attachment.url
    if (!key) return null

    const mime = (attachment.mime || "").toLowerCase()
    if (mime === "pdf" || mime.includes("pdf")) {
      return { url: await pdfs.getSignedUrl(key), download: false, newTab: true }
    }
    if (mime === "image" || mime.startsWith("image/")) {
      return { url: await images.getSignedUrl(key), download: true, newTab: true }
    }
    if (mime === "video" || mime.startsWith("video/")) {
      try {
        const signed = await media.getSignedUrl(key)
        if (signed) return { url: signed, download: false, newTab: true }
      } catch {
        // fallback below
      }
      return { url: await videos.getSignedUrl(key), download: false, newTab: true }
    }

    // Fallback: try generic media signed URL; if it fails, open as-is.
    try {
      const signed = await media.getSignedUrl(key)
      if (signed) return { url: signed, download: true, newTab: true }
    } catch {
      // ignore and fall back
    }
    return { url: key, download: true, newTab: true }
  }

  const handleAttachmentClick = async (e: React.MouseEvent, attachment: Attachment) => {
    e.preventDefault()
    e.stopPropagation()
    setAttachmentError("")
    try {
      const resolved = await resolveAttachmentLink(attachment)
      if (!resolved?.url) throw new Error("Unable to open attachment")
      const link = document.createElement("a")
      link.href = resolved.url
      link.target = resolved.newTab ? "_blank" : "_self"
      link.rel = resolved.newTab ? "noopener noreferrer" : ""
      if (resolved.download) {
        link.download = getAttachmentDisplayName(attachment.originalFilename, attachment.key, attachment.name)
      }
      link.click()
    } catch (err: any) {
      setAttachmentError(err?.message || "Unable to open attachment")
    }
  }

  const handleJoinVirtual = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (event) onJoinMeeting(event)
  }

  const handleCopyMeetingLink = async (e: React.MouseEvent, meetingId: string, guestJoinUrl?: string | null) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await copyTextToClipboard(getMeetingUrl(meetingId, guestJoinUrl))
      setMeetingLinkCopyStatus("copied")
    } catch {
      setMeetingLinkCopyStatus("error")
    }
  }

  const handleCopyDetailsLink = async (e: React.MouseEvent, meetingId: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (!buildMeetingDetailsUrl) return
    try {
      await copyTextToClipboard(toAbsoluteUrl(buildMeetingDetailsUrl(meetingId)))
      setDetailsLinkCopyStatus("copied")
    } catch {
      setDetailsLinkCopyStatus("error")
    }
  }

  const handleToggleAttendance = (attendeeId: string, locked: boolean) => {
    if (locked) return
    setAttendanceChecked((prev) => {
      const next = new Set(prev)
      if (next.has(attendeeId)) {
        next.delete(attendeeId)
      } else {
        next.add(attendeeId)
      }
      return next
    })
  }

  const handleAddManualGuest = () => {
    const displayName = normalizeGuestName(manualGuestNameInput)
    if (!displayName) return
    setManualGuestNames((prev) => {
      const existing = new Set(prev.map((name) => normalizeGuestName(name).toLocaleLowerCase()))
      if (existing.has(displayName.toLocaleLowerCase())) return prev
      return [...prev, displayName]
    })
    setManualGuestNameInput("")
  }

  const handleRemoveManualGuest = (index: number) => {
    setManualGuestNames((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleSaveAttendance = async () => {
    if (!event?.id) return
    setAttendanceSaving(true)
    setAttendanceError("")
    try {
      const attendedIds = attendanceEntries
        .filter((entry) => (entry.attendee_type || "user") === "user")
        .filter((entry) => attendanceChecked.has(getAttendanceEntryId(entry)))
        .map((entry) => entry.user_id)
        .filter((userId): userId is string | number => userId !== null && userId !== undefined)
      const manualGuestAttendees: ManualGuestAttendee[] = manualGuestNames
        .map((displayName) => ({ display_name: normalizeGuestName(displayName) }))
        .filter((guest) => Boolean(guest.display_name))
      const data = await meetings.saveMeetingAttendance(event.id, attendedIds, manualGuestAttendees)
      const entries = Array.isArray(data.attendees) ? data.attendees : []
      setAttendanceEntries(entries)
      const checked = new Set(entries.filter((entry) => entry.attended).map((entry) => getAttendanceEntryId(entry)))
      setAttendanceChecked(checked)
      setManualGuestNames(
        entries
          .filter((entry) => entry.attendee_type === "guest" && !entry.locked)
          .map((entry) => normalizeGuestName(entry.display_name || "Guest"))
          .filter(Boolean),
      )
      setManualGuestNameInput("")
      if (onAttendanceUpdated) {
        onAttendanceUpdated(event.id, entries.some((entry) => entry.attended))
      }
    } catch (err: any) {
      setAttendanceError(err?.response?.data?.detail || err?.message || "Unable to save attendance.")
    } finally {
      setAttendanceSaving(false)
    }
  }

  const handleEndMeeting = async () => {
    if (!event?.id || !onEndMeeting) return
    setEndingMeeting(true)
    setEndMeetingError("")
    try {
      await onEndMeeting(event.id)
    } catch (err: any) {
      setEndMeetingError(err?.response?.data?.detail || err?.message || "Unable to end meeting.")
    } finally {
      setEndingMeeting(false)
    }
  }

  const handleDownloadTranscript = async () => {
    if (!event?.id) return
    setTranscriptError("")
    setTranscriptDownloading(true)
    try {
      const blob = await meetings.downloadTranscript(event.id)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `meeting-${event.id}-transcript.txt`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      setTranscriptError(err?.response?.data?.detail || err?.message || "Unable to download transcript.")
    } finally {
      setTranscriptDownloading(false)
    }
  }

  const handleDownloadRecording = async () => {
    if (!event?.id) return
    setRecordingError("")
    setRecordingDownloading(true)
    try {
      const response = await meetings.downloadRecording(event.id)
      if (!response.url) throw new Error("Recording is not available.")
      const link = document.createElement("a")
      link.href = response.url
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.download = response.filename || `${event.title || "Meeting"} recording.mp4`
      link.click()
    } catch (err: any) {
      setRecordingError(err?.response?.data?.detail || err?.message || "Unable to download recording.")
    } finally {
      setRecordingDownloading(false)
    }
  }

  if (!event) return null

  const timeRangeLabel =
    event.startTimeIso && event.endTimeIso
      ? (() => {
          const start = new Date(event.startTimeIso)
          const end = new Date(event.endTimeIso)
          const sameDay = meetingDateKey(start, eventTimeZone) === meetingDateKey(end, eventTimeZone)
          return sameDay
            ? `${formatTime(event.startTimeIso)} - ${formatTime(event.endTimeIso)}`
            : `${formatDateTime(event.startTimeIso)} - ${formatDateTime(event.endTimeIso)}`
        })()
      : event.time
  const timeZoneLabel = event.startTimeIso ? meetingTimeZoneLabel(new Date(event.startTimeIso), eventTimeZone) : eventTimeZone
  const hostMember = rosterById.get(String(event.createdById || ""))
  const hostName = hostMember?.name || "Host"
  const inviteeRoster =
    event.invitedUserIds && event.invitedUserIds.length > 0
      ? event.invitedUserIds
          .map((id) => rosterById.get(String(id)))
          .filter((member): member is RosterMember => Boolean(member))
      : roster
  const visibleInvitees = inviteeRoster.slice(0, 4)
  const inviteeOverflow = Math.max(0, inviteeRoster.length - visibleInvitees.length)
  const attendedCount = attendanceEntries.filter((entry) => entry.attended).length
  const attendanceTotal = attendanceEntries.length || inviteeRoster.length
  const canEditEvent = canReviewQuestions && popupState !== "past"
  const occurrenceStatus = event.occurrenceStatus || (event.status === "cancelled" ? "cancelled" : event.isException ? "edited" : "normal")
  const renderOccurrenceBadge = () => {
    if (!event.recurrence || occurrenceStatus === "normal") return null
    const isCancelled = occurrenceStatus === "cancelled"
    return (
      <Badge
        variant="outline"
        className={`border-white/20 bg-white/10 text-[var(--cohort-hero-ink)] ${
          isCancelled
            ? "border-[rgba(255,214,202,0.34)] bg-[rgba(138,58,34,0.45)]"
            : "border-[rgba(244,224,171,0.3)] bg-[rgba(122,92,18,0.34)]"
        }`}
      >
        {isCancelled ? <X className="mr-1 h-3.5 w-3.5" /> : <Edit3 className="mr-1 h-3.5 w-3.5" />}
        {isCancelled ? "Cancelled occurrence" : "Edited occurrence"}
      </Badge>
    )
  }
  const recordingPreparing = Boolean(event.recordingEnabled && !event.recordingAvailable && event.recordingStatus !== "failed")
  const recordingFailed = Boolean(event.recordingEnabled && event.recordingStatus === "failed")
  const resourceLabel = popupState === "past" ? "Resources from the session" : popupState === "soon" || popupState === "live" || popupState === "rejoin" ? "Before we meet" : "Resources"
  const detailsLinkButton = buildMeetingDetailsUrl ? (
    <button
      type="button"
      aria-label={detailsLinkCopyStatus === "copied" ? "Meeting details link copied" : "Copy meeting details link"}
      title={detailsLinkCopyStatus === "copied" ? "Meeting details link copied" : "Copy meeting details link"}
      onClick={(e) => handleCopyDetailsLink(e, event.id)}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[var(--cohort-hero-muted)] transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
    >
      {detailsLinkCopyStatus === "copied" ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
    </button>
  ) : null
  const detailsLinkError =
    detailsLinkCopyStatus === "error" ? (
      <span className="text-[11px] font-medium text-[var(--cohort-clay)]">Copy failed</span>
    ) : null

  const renderLocationCard = () => (
    <div className={`${popupCardClass} space-y-3`}>
      <p className={popupCapClass}>Where</p>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--cohort-wash)] text-[var(--cohort-forest)]">
          {event.isVirtual ? <Video className="h-5 w-5" /> : <MapPin className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-[var(--cohort-ink)]">
            {event.isVirtual ? "Virtual room" : event.location || "Location TBD"}
          </p>
          <p className="truncate text-xs text-[var(--cohort-ink-muted)]">
            {event.isVirtual ? getMeetingUrl(event.id, event.guestAccessEnabled ? event.guestJoinUrl : null) : event.location || "Add a place before the session"}
          </p>
        </div>
        {event.isVirtual && popupState !== "past" && (
          <Button
            type="button"
            variant="secondary"
            size="auto"
            onClick={(e) => handleCopyMeetingLink(e, event.id, event.guestAccessEnabled ? event.guestJoinUrl : null)}
            aria-label={meetingLinkCopyStatus === "copied" ? "Meeting link copied" : "Copy meeting link"}
            className={`${popupPillButtonClass} cohort-secondary-button px-3 py-1.5 text-xs`}
          >
            {meetingLinkCopyStatus === "copied" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {meetingLinkCopyStatus === "copied" ? "Copied" : "Copy"}
          </Button>
        )}
      </div>
      {meetingLinkCopyStatus === "error" && <p className="text-xs text-[var(--cohort-clay)]">Copy failed</p>}
    </div>
  )

  const renderWhoCard = () => (
    <div className={`${popupCardClass} space-y-3`}>
      <p className={popupCapClass}>Who</p>
      <div className="flex items-center gap-3">
        <AvatarBubble name={hostName} seed={event.createdById || hostName} size="lg" avatarUrl={getRosterAvatarUrl(hostMember)} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-[var(--cohort-ink)]">{hostName}</p>
          <p className="text-xs text-[var(--cohort-ink-muted)]">Host</p>
        </div>
      </div>
      <div className="flex items-center gap-3 whitespace-nowrap">
        <span className="flex items-center">
          {visibleInvitees.map((member, index) => (
            <span key={String(member.id)} className={index > 0 ? "-ml-2" : ""}>
              <AvatarBubble name={member.name} seed={member.id} size="sm" avatarUrl={getRosterAvatarUrl(member)} />
            </span>
          ))}
          {inviteeOverflow > 0 && (
            <span className="-ml-2 inline-flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-white bg-[var(--cohort-wash)] px-2 text-[11px] font-semibold text-[var(--cohort-ink-muted)]">
              +{inviteeOverflow}
            </span>
          )}
        </span>
        <span className="text-xs text-[var(--cohort-ink-muted)]">
          {event.inviteesMode === "mentors"
            ? getInviteeBucketLabel("mentors", roleLabels)
            : event.inviteesMode === "scholars"
              ? getInviteeBucketLabel("scholars", roleLabels)
              : event.inviteesMode === "custom"
                ? `${inviteeRoster.length} invited`
                : event.inviteesMode === "cohort"
                  ? "Entire cohort"
                  : "Invitees TBD"}
        </span>
      </div>
    </div>
  )

  const renderDescriptionCard = () =>
    event.description ? (
      <div className={`${popupCardClass} space-y-2`}>
        <p className={popupCapClass}>What we'll do</p>
        <p className="text-sm leading-6 text-[var(--cohort-ink-muted)]">{event.description}</p>
      </div>
    ) : null

  const renderResourcesCard = () =>
    event.attachments && event.attachments.length > 0 ? (
      <div className={`${popupCardClass} space-y-3`}>
        <div className="flex items-center justify-between gap-3">
          <p className={popupCapClass}>{resourceLabel}</p>
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[var(--cohort-wash)] px-2 text-[11px] font-semibold text-[var(--cohort-ink-muted)]">
            {event.attachments.length}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {event.attachments.map((att) => {
            const label = getAttachmentDisplayName(att.originalFilename, att.key, att.name)
            const isVideo = (att.mime || "").toLowerCase().includes("video")
            return (
              <button
                key={att.key}
                type="button"
                className="flex min-w-0 items-center gap-3 rounded-xl border border-[var(--cohort-rule)] bg-white p-3 text-left transition-colors hover:border-[var(--cohort-rule-strong)] hover:bg-[var(--cohort-wash)]"
                onClick={(e) => handleAttachmentClick(e, att)}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--cohort-wash)] text-[var(--cohort-forest)]">
                  {isVideo ? <Film className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-[var(--cohort-ink)]">{label}</span>
                  <span className="block text-xs text-[var(--cohort-ink-muted)]">Resource</span>
                </span>
              </button>
            )
          })}
        </div>
        {attachmentError && <p className="text-xs text-[var(--cohort-clay)]">{attachmentError}</p>}
      </div>
    ) : null

  const renderAttendanceGrid = () => {
    const placeholderRows = [0, 1, 2, 3]
    const entries =
      attendanceEntries.length > 0
        ? attendanceEntries.map((entry) => {
            const id = getAttendanceEntryId(entry)
            const member = getAttendanceRosterMember(entry, rosterById)
            return {
              id,
              name: getAttendanceEntryDisplayName(entry, rosterById),
              role: getAttendanceEntryRole(entry, rosterById),
              attended: entry.attended,
              member,
            }
          })
        : inviteeRoster.map((member) => ({
            id: String(member.id),
            name: member.name,
            role: member.role || "Invitee",
            attended: false,
            member,
          }))
    return (
      <div className={`${popupCardClass} space-y-3`}>
        <p className={popupCapClass}>Who was there</p>
        <div className="grid min-h-[206px] grid-cols-1 gap-1.5 sm:min-h-[105px] sm:grid-cols-2">
          {entries.length === 0
            ? placeholderRows.map((index) => (
              <div
                key={`attendance-placeholder-${index}`}
                data-testid="attendance-placeholder-row"
                aria-hidden="true"
                className="flex min-h-[48px] animate-pulse items-center gap-3 rounded-[10px] bg-[var(--cohort-wash)] p-2.5"
              >
                <span className="h-7 w-7 shrink-0 rounded-full bg-[var(--cohort-muted-fill)]" />
                <span className="min-w-0 flex-1 space-y-1.5">
                  <span className="block h-3 w-28 rounded-full bg-[var(--cohort-muted-fill)]" />
                  <span className="block h-2.5 w-16 rounded-full bg-[var(--cohort-muted-fill)] opacity-70" />
                </span>
                <span className="ml-auto h-2 w-2 rounded-full bg-[var(--cohort-muted-fill)]" />
              </div>
            ))
            : entries.map((entry) => (
              <div
                key={entry.id}
                className={`flex min-h-[48px] items-center gap-3 rounded-[10px] p-2.5 ${
                  entry.attended ? "bg-[var(--cohort-success-soft)]" : "bg-white"
                }`}
              >
                <AvatarBubble name={entry.name} seed={entry.id} size="sm" muted={!entry.attended} avatarUrl={getRosterAvatarUrl(entry.member)} />
                <div className="min-w-0">
                  <p className={`truncate text-sm font-medium ${entry.attended ? "text-[var(--cohort-ink)]" : "text-[var(--cohort-ink-muted)]"}`}>
                    {entry.name}
                  </p>
                  <p className="text-xs text-[var(--cohort-ink-muted)]">{entry.role}</p>
                </div>
                <span className={`ml-auto h-2 w-2 rounded-full ${entry.attended ? "bg-[var(--cohort-success)]" : "bg-[var(--cohort-muted-fill)]"}`} />
              </div>
            ))}
        </div>
      </div>
    )
  }

  const renderAttendanceEditor = () => (
    <div className={`${popupCardClass} space-y-3`}>
      <div className="flex items-center justify-between gap-3">
        <p className={popupCapClass}>Review attendance</p>
        <Button type="button" variant="secondary" size="auto" onClick={() => setAttendanceReviewOpen(false)} className="cohort-secondary-button !min-h-9 px-3 text-xs">
          Back
        </Button>
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--cohort-ink-muted)]" />
        <Input className="rounded-xl bg-white pl-9" placeholder="Search attendees..." readOnly />
      </div>
      {attendanceLoading ? (
        <p className="text-sm text-[var(--cohort-ink-muted)]">Loading attendance...</p>
      ) : attendanceEntries.length === 0 && manualGuestNames.length === 0 ? (
        <p className="text-sm text-[var(--cohort-ink-muted)]">No invitees to track for this meeting.</p>
      ) : (
        <div className="space-y-2">
          {attendanceEntries
            .filter((entry) => !(entry.attendee_type === "guest" && !entry.locked))
            .map((entry) => {
            const entryId = getAttendanceEntryId(entry)
            const member = getAttendanceRosterMember(entry, rosterById)
            const displayName = getAttendanceEntryDisplayName(entry, rosterById)
            const locked = Boolean(entry.locked)
            const sourceLabel =
              entry.source === "daily" || entry.source === "meet_audit"
                ? "Automatic"
                : entry.source === "manual"
                  ? "Manual"
                  : ""
            return (
              <div key={entryId} className="flex items-center justify-between gap-3 rounded-xl bg-white p-3">
                <label className="flex items-center gap-3 text-sm text-[var(--cohort-ink)]">
                  <Checkbox
                    checked={entry.attendee_type === "guest" ? true : attendanceChecked.has(entryId)}
                    onCheckedChange={() => handleToggleAttendance(entryId, locked)}
                    disabled={locked || attendanceSaving || entry.attendee_type === "guest"}
                  />
                  <AvatarBubble name={displayName} seed={entryId} size="sm" avatarUrl={getRosterAvatarUrl(member)} />
                  <span>{displayName}{entry.is_host ? " (Host)" : ""}</span>
                </label>
                {sourceLabel && (
                  <Badge variant="outline" className={locked ? "bg-[var(--cohort-wash)] text-[var(--cohort-ink-muted)]" : "bg-white text-[var(--cohort-ink-muted)]"}>
                    {sourceLabel}
                  </Badge>
                )}
              </div>
            )
          })}
          {manualGuestNames.map((displayName, index) => (
            <div key={`${displayName}-${index}`} className="flex items-center justify-between gap-3 rounded-xl bg-white p-3">
              <div className="flex items-center gap-3 text-sm text-[var(--cohort-ink)]">
                <Checkbox checked disabled />
                <AvatarBubble name={displayName} seed={`manual-guest-${displayName}`} size="sm" />
                <span>{displayName}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove ${displayName}`}
                onClick={() => handleRemoveManualGuest(index)}
                disabled={attendanceSaving}
                className="h-8 w-8 rounded-lg text-[var(--cohort-ink-muted)] hover:bg-[var(--cohort-clay-soft)] hover:text-[var(--cohort-clay)]"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-2 rounded-xl bg-[var(--cohort-wash)] p-3 sm:flex-row">
        <Input
          value={manualGuestNameInput}
          onChange={(event) => setManualGuestNameInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              handleAddManualGuest()
            }
          }}
          placeholder="Guest display name"
          disabled={attendanceSaving}
          className="rounded-xl bg-white"
        />
        <Button
          type="button"
          variant="secondary"
          size="auto"
          onClick={handleAddManualGuest}
          disabled={attendanceSaving || !normalizeGuestName(manualGuestNameInput)}
          className="cohort-secondary-button rounded-xl"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add guest
        </Button>
      </div>
      {attendanceError && <p className="text-xs text-[var(--cohort-clay)]">{attendanceError}</p>}
      <Button
        size="auto"
        onClick={handleSaveAttendance}
        disabled={attendanceLoading || attendanceSaving}
        className={`${popupPillButtonClass} cohort-primary-button self-start`}
      >
        {attendanceSaving ? "Saving..." : "Save attendance"}
      </Button>
    </div>
  )

  return (
    <AnimatedDialog
      onClose={onClose}
      hideCloseButton
      className={`${popupShellClass} cohort-dialog cohort-dialog--framed cohort-meeting-details`}
    >
      <DialogHeader className="sr-only">
        <DialogTitle>{event.title}</DialogTitle>
      </DialogHeader>
      <DialogDescription className="sr-only">
        Event details for {event.title}
      </DialogDescription>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="cohort-meeting-details__close absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-[var(--cohort-hero-ink)] backdrop-blur-md transition-colors hover:bg-white/20"
      >
        <X className="h-4 w-4" />
      </button>
      <div data-testid="meeting-details-hero" className={`${popupHeroClass} px-5 pb-6 pt-5 sm:px-7 sm:pb-7 sm:pt-6`}>
        <div className="flex min-h-8 flex-wrap items-center gap-2 pr-11" data-testid="meeting-details-header-meta">
          <p className="text-[11px] font-semibold uppercase leading-none tracking-[0.1em] text-[var(--cohort-hero-eyebrow)]">
            {popupState === "past"
              ? "Completed"
              : popupState === "rejoin"
                ? "Still active"
                : popupState === "live"
                  ? "Happening now"
                  : popupState === "soon"
                    ? "Starting soon"
                    : event.type || "Upcoming meeting"}
          </p>
          {detailsLinkButton}
          {detailsLinkError}
          {renderOccurrenceBadge()}
        </div>
        <div className="mt-3 flex items-start gap-4 sm:mt-4 sm:gap-5">
          <DateStamp iso={event.startTimeIso} timeZone={eventTimeZone} />
          <div className="min-w-0 flex-1">
            <h2 className="text-balance font-headings-h2 text-[27px] font-normal leading-[1.16] tracking-[-0.035rem] text-[var(--cohort-hero-ink)] sm:text-[30px]">
              {event.title}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm font-medium text-[var(--cohort-hero-muted)]">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {timeRangeLabel}{timeZoneLabel ? ` ${timeZoneLabel}` : ""}
              </span>
              {event.recurrence && (
                <span className="inline-flex items-center gap-1.5">
                  <Repeat className="h-4 w-4" />
                  {formatRecurrence(event.recurrence)}
                </span>
              )}
            </div>

            {popupState === "soon" && (
              <div className="mt-4 flex items-baseline gap-2.5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--cohort-hero-eyebrow)]">Starts in</span>
                <span className="font-headings-h2 text-[34px] leading-none tracking-[-0.04em] text-[var(--cohort-hero-ink)]">
                  {formatCountdown(secondsToStart)}
                </span>
              </div>
            )}

            {(popupState === "live" || popupState === "rejoin") && (
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--cohort-hero-ink)]">
                <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--cohort-hero-eyebrow)]" />
                {popupState === "rejoin" ? "Still active" : "Live now"}
              </div>
            )}

            {(popupState === "soon" || popupState === "live" || popupState === "rejoin") && (
              <div className="mt-4 flex items-center gap-3">
                <span className="flex items-center">
                  {visibleInvitees.slice(0, 3).map((member, index) => (
                    <span key={String(member.id)} className={index > 0 ? "-ml-2" : ""}>
                      <AvatarBubble name={member.name} seed={member.id} size="lg" avatarUrl={getRosterAvatarUrl(member)} />
                    </span>
                  ))}
                  {inviteeOverflow > 0 && (
                    <span className="-ml-2 inline-flex h-9 min-w-9 items-center justify-center rounded-full border-2 border-[var(--cohort-hero)] bg-[var(--cohort-sage)] px-2 text-xs font-semibold text-white">
                      +{inviteeOverflow}
                    </span>
                  )}
                </span>
                <div className="min-w-0 text-sm text-[var(--cohort-hero-muted)]">
                  <p className="truncate">{visibleInvitees.length > 0 ? `${visibleInvitees.map((member) => member.name.split(" ")[0]).slice(0, 3).join(", ")} are invited` : "Members are invited"}</p>
                  <p className="truncate text-xs text-[var(--cohort-hero-eyebrow)]">{hostName} will host</p>
                </div>
              </div>
            )}

            {popupState === "past" && (
              <div className="mt-4 flex items-center gap-2 text-sm text-[var(--cohort-hero-muted)]">
                <AvatarBubble name={hostName} seed={event.createdById || hostName} avatarUrl={getRosterAvatarUrl(hostMember)} />
                <span>Hosted by {hostName}{attendanceTotal ? ` · ${attendedCount} of ${attendanceTotal} attended` : ""}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div data-testid="meeting-details-body" className="cohort-meeting-details__body flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
        {adminIsNotInvited ? (
          <div role="note" className="flex items-start gap-3 rounded-[14px] border border-[var(--cohort-rule)] bg-[var(--cohort-wash)] px-4 py-3 text-sm leading-5 text-[var(--cohort-ink-muted)]">
            <span aria-hidden="true" className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[var(--cohort-rule-strong)] bg-white text-xs font-semibold text-[var(--cohort-ink)]">i</span>
            <span>You can manage this event as a cohort administrator, but you’re not on the invite list.</span>
          </div>
        ) : null}
        {occurrenceStatus === "cancelled" && (
          <div className="flex items-center gap-3 rounded-[16px] border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-white/70 text-[var(--cohort-clay)]">
              <X className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-[var(--cohort-ink)]">This occurrence was cancelled</span>
              <span className="block text-xs text-[var(--cohort-ink-muted)]">The rest of the series is unaffected.</span>
              {restoreError && <span className="mt-1 block text-xs text-[var(--cohort-clay)]">{restoreError}</span>}
            </span>
            {onRestoreOccurrence && (
              <Button
                type="button"
                variant="secondary"
                size="auto"
                disabled={restoringOccurrence}
                className={`${popupPillButtonClass} cohort-secondary-button px-3 py-1.5 text-xs`}
                onClick={async () => {
                  setRestoreError("")
                  setRestoringOccurrence(true)
                  try {
                    await onRestoreOccurrence(event)
                  } catch (err: any) {
                    setRestoreError(err?.response?.data?.detail || err?.message || "Unable to restore occurrence.")
                  } finally {
                    setRestoringOccurrence(false)
                  }
                }}
              >
                {restoringOccurrence ? "Restoring..." : "Restore"}
              </Button>
            )}
          </div>
        )}
        {attendanceReviewOpen ? (
          renderAttendanceEditor()
        ) : popupState === "past" ? (
          <>
            <div className="space-y-3 rounded-[16px] border border-[rgba(122,92,18,0.2)] bg-[var(--cohort-ochre-soft)] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 text-[var(--cohort-ochre)]">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em]">Wise friend's notes</p>
                </div>
                {canDownloadTranscript && (
                  <button
                    type="button"
                    onClick={handleDownloadTranscript}
                    disabled={transcriptDownloading}
                    className="cohort-secondary-button inline-flex !min-h-9 items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold"
                    title="Download transcript"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {transcriptDownloading ? "Preparing..." : "Transcript"}
                  </button>
                )}
                {canDownloadRecording && event.recordingAvailable && (
                  <button
                    type="button"
                    onClick={handleDownloadRecording}
                    disabled={recordingDownloading}
                    className="cohort-secondary-button inline-flex !min-h-9 items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold"
                    title="Download recording"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {recordingDownloading ? "Preparing..." : "Recording"}
                  </button>
                )}
              </div>
              <p className="whitespace-pre-line font-headings-h3 text-[17px] font-normal leading-7 text-[var(--cohort-ink)]">
                {event.summaryText || "Summary not available yet."}
              </p>
              {event.isVirtual ? <p className="text-xs leading-5 text-[var(--cohort-ink-muted)]">Main-room conversation only. Mentoring breakout notes are kept in each relationship’s recap.</p> : null}
              {recordingPreparing && (
                <p className="text-xs text-[var(--cohort-ink-muted)]">Recording is being prepared.</p>
              )}
              {recordingFailed && (
                <p className="text-xs text-[var(--cohort-clay)]">Recording could not be prepared.</p>
              )}
              {transcriptError && <p className="text-xs text-[var(--cohort-clay)]">{transcriptError}</p>}
              {recordingError && <p className="text-xs text-[var(--cohort-clay)]">{recordingError}</p>}
            </div>
            {onOpenContinuity ? continuityRelationships.filter(relationship => relationship.recaps.some(recap => recap.meeting_id === event.id)).map(relationship => <button key={relationship.id} type="button" className="cohort-row-action flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border border-[var(--cohort-rule)] bg-[var(--cohort-rest)] px-4 py-3 text-left" onClick={() => { onClose(); window.setTimeout(() => onOpenContinuity(relationship.id), 0) }}>
              <span><span className="block text-sm font-medium">{String(relationship.mentee.id) === currentUserId ? "Your relationship recap" : `${relationship.mentee.name} & ${relationship.mentor.name}`}</span><span className="mt-1 block text-xs text-[var(--cohort-ink-muted)]">Open recap and For next time</span></span><span aria-hidden="true">›</span>
            </button>) : null}
            {renderAttendanceGrid()}
            {canReviewQuestions && (
              <><MeetingInspireHistoryPanel meetingId={event.id} />{event.isVirtual && <MeetingPollHistoryPanel meetingId={event.id} />}</>
            )}
            <MeetingQuestionsPanel
              meetingId={event.id}
              collectionEnabled={false}
              canReview={!!canReviewQuestions}
              submissionCount={event.questionSubmissionCount || 0}
              unreviewedCount={event.unreviewedQuestionSubmissionCount || 0}
              onCountsUpdated={onQuestionCountsUpdated}
              past
            />
            {renderResourcesCard()}
          </>
        ) : popupState === "soon" || popupState === "live" || popupState === "rejoin" ? (
          <>
            {renderDescriptionCard()}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {renderLocationCard()}
              {renderWhoCard()}
            </div>
            {renderResourcesCard()}
            <MeetingQuestionsPanel
              meetingId={event.id}
              collectionEnabled={!!event.questionCollectionEnabled}
              canReview={!!canReviewQuestions}
              submissionCount={event.questionSubmissionCount || 0}
              unreviewedCount={event.unreviewedQuestionSubmissionCount || 0}
              onCountsUpdated={onQuestionCountsUpdated}
            />
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {renderLocationCard()}
              {renderWhoCard()}
            </div>
            {renderDescriptionCard()}
            {renderResourcesCard()}
            <MeetingQuestionsPanel
              meetingId={event.id}
              collectionEnabled={!!event.questionCollectionEnabled}
              canReview={!!canReviewQuestions}
              submissionCount={event.questionSubmissionCount || 0}
              unreviewedCount={event.unreviewedQuestionSubmissionCount || 0}
              onCountsUpdated={onQuestionCountsUpdated}
            />
          </>
        )}
      </div>

      <div data-testid="meeting-details-actions" className="cohort-meeting-details__actions flex shrink-0 flex-wrap items-center gap-2 border-t border-[var(--cohort-rule)] bg-white px-5 py-4 sm:px-6">
        <Button type="button" variant="secondary" size="auto" onClick={onClose} className={`${popupPillButtonClass} cohort-secondary-button cohort-meeting-details__action`}>
          Close
        </Button>
        {canEndMeeting && (
          <Button
            type="button"
            variant="secondary"
            onClick={handleEndMeeting}
            className={`${popupPillButtonClass} cohort-destructive-secondary-button cohort-meeting-details__action`}
            disabled={endingMeeting}
            size="auto"
          >
            {endingMeeting ? "Ending..." : "End meeting early"}
          </Button>
        )}
        <span className="cohort-meeting-details__spacer flex-1" />
        {popupState === "past" && canManageAttendance && !attendanceReviewOpen && (
          <Button type="button" variant="secondary" size="auto" onClick={() => setAttendanceReviewOpen(true)} className={`${popupPillButtonClass} cohort-secondary-button cohort-meeting-details__action`}>
            <Edit3 className="h-4 w-4" />
            Review attendance
          </Button>
        )}
        {canEditEvent && onEdit && (
          <Button type="button" variant="secondary" size="auto" onClick={() => onEdit(event)} className={`${popupPillButtonClass} cohort-secondary-button cohort-meeting-details__action`}>
            <Edit3 className="h-4 w-4" />
            Edit
          </Button>
        )}
        {(popupState === "soon" || popupState === "live" || popupState === "rejoin") && event.isVirtual && !adminIsNotInvited && (
          <Button type="button" size="auto" onClick={handleJoinVirtual} className="cohort-primary-button cohort-meeting-details__action cohort-meeting-details__primary inline-flex min-h-11 items-center justify-center gap-2 px-4 py-2 text-sm font-semibold">
            <Video className="h-4 w-4" />
            {popupState === "rejoin" ? "Rejoin virtual session" : "Join virtual session"}
          </Button>
        )}
      </div>
      {endMeetingError && <p className="px-6 pb-3 text-xs text-[var(--cohort-clay)]">{endMeetingError}</p>}
    </AnimatedDialog>
  )
}

export const EventsListCard = forwardRef<EventsListCardHandle, EventsListCardProps>(function EventsListCard({
  title = "Events & Meetings",
  events,
  loading = false,
  error,
  showPast,
  onTogglePast,
  showRequiresAttendance = false,
  onToggleRequiresAttendance,
  onSelectEvent,
  canCreateEvent,
  requiresAttendanceLabel = "Requires attendance",
  loadingLabel = "Loading meetings...",
  emptyLabel = "No meetings to show.",
  isAdmin = false,
  currentUserId,
  cohortId,
  roster = [],
  roleLabels,
  deepLinkedMeetingId,
  buildMeetingDetailsUrl,
  onMeetingPopupUrlChange,
  onEventsChange,
  continuityRelationships = [],
  onOpenContinuity,
  className,
}: EventsListCardProps, ref) {
  const navigate = useNavigate()
  const { timezone: profileTimeZone } = useTimezone()
  const [selectedEvent, setSelectedEvent] = useState<EventsListItem | null>(null)
  const [eventEditorOpen, setEventEditorOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState<EventsListItem | null>(null)
  const [eventTitleTouched, setEventTitleTouched] = useState(false)
  const [eventWhereTouched, setEventWhereTouched] = useState(false)
  const [eventInviteesTouched, setEventInviteesTouched] = useState(false)
  const [eventSaveAttempted, setEventSaveAttempted] = useState(false)
  const [internalEvents, setInternalEvents] = useState<EventsListItem[]>(() => events.filter(isVisibleMeetingEvent))
  const internalEventsRef = useRef<EventsListItem[]>(events.filter(isVisibleMeetingEvent))
  const [eventDate, setEventDate] = useState<string>("")
  const [eventStartTime, setEventStartTime] = useState<string>("")
  const [eventEndDate, setEventEndDate] = useState<string>("")
  const [eventEndTime, setEventEndTime] = useState<string>("")
  const [timeError, setTimeError] = useState<string>("")
  const [eventSaveError, setEventSaveError] = useState<string>("")
  const [useCustomRange, setUseCustomRange] = useState(false)
  const [recurrenceDraft, setRecurrenceDraft] = useState<RecurrenceRule | null>(null)
  const [recurrenceError, setRecurrenceError] = useState<string>("")
  const [mobileRecurrenceExpanded, setMobileRecurrenceExpanded] = useState(false)
  const [editScope, setEditScope] = useState<RecurrenceEditScope | null>(null)
  const [scopeChooserKind, setScopeChooserKind] = useState<RecurrenceScopeChooserKind | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string>("")
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [initialEditSnapshot, setInitialEditSnapshot] = useState<string | null>(null)
  const [originalEditStartIso, setOriginalEditStartIso] = useState<string | null>(null)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [attachmentError, setAttachmentError] = useState<string>("")
  const [savingEvent, setSavingEvent] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const saveInFlightRef = useRef(false)
  const [inviteesMode, setInviteesMode] = useState<InviteeSelectionMode>("")
  const [selectedInvitees, setSelectedInvitees] = useState<string[]>([])
  const [inviteSearch, setInviteSearch] = useState("")
  const [editorInPersonEnabled, setEditorInPersonEnabled] = useState(false)
  const dailyCheckInOpen = useSyncExternalStore(
    subscribeToDailyCheckInVisibility,
    getDailyCheckInOpen,
    getDailyCheckInOpen,
  )

  const sortEventsByStart = (items: EventsListItem[]) =>
    [...items].sort((a, b) => {
      const aStart = a.startTimeIso ? new Date(a.startTimeIso).getTime() : Number.POSITIVE_INFINITY
      const bStart = b.startTimeIso ? new Date(b.startTimeIso).getTime() : Number.POSITIVE_INFINITY
      return aStart - bStart
    })

  const commitEvents = useCallback(
    (updater: (events: EventsListItem[]) => EventsListItem[]) => {
      const next = updater(internalEventsRef.current).filter(isVisibleMeetingEvent)
      internalEventsRef.current = next
      setInternalEvents(next)
      onEventsChange?.(next)
    },
    [onEventsChange],
  )

  const activeEditTimeZone = resolveTimeZone(editingEvent?.timeZone)

  const toDateInputInZone = (isoString: string, timeZone?: string | null) =>
    toDateInputInTimeZone(isoString, timeZone)

  const normalizeRecurrenceForDirtyCheck = (recurrence?: RecurrenceRule | null) =>
    recurrence
      ? {
          interval: recurrence.interval || 1,
          period: recurrence.period,
          daysOfWeek: [...(recurrence.daysOfWeek || [])].sort(),
          startDate: recurrence.startDate || "",
          endDate: recurrence.endDate || "",
          noEndDate: Boolean(recurrence.noEndDate),
        }
      : null

  const buildEditSnapshot = ({
    event,
    date,
    startTime,
    endDate,
    endTime,
    mode,
    selected,
    inPersonEnabled,
  }: {
    event: EventsListItem
    date: string
    startTime: string
    endDate: string
    endTime: string
    mode: InviteeSelectionMode
    selected: string[]
    inPersonEnabled: boolean
  }) =>
    JSON.stringify({
      title: (event.title || "").trim(),
      description: event.description || "",
      location: inPersonEnabled ? event.location || "" : "",
      inPersonEnabled,
      date,
      startTime,
      endDate,
      endTime,
      timeZone: resolveTimeZone(event.timeZone),
      isVirtual: !!event.isVirtual,
      isMilestone: !!event.isMilestone,
      guestAccessEnabled: !!event.guestAccessEnabled,
      waitingRoomEnabled: !!event.waitingRoomEnabled,
      questionCollectionEnabled: !!event.questionCollectionEnabled,
      recordingEnabled: !!event.recordingEnabled,
      inviteesMode: mode,
      selectedInvitees: mode === "custom" ? [...selected].map(String).sort() : [],
      attachmentIds: normalizeAttachmentIds(event.attachments).sort(),
      recurrence: normalizeRecurrenceForDirtyCheck(event.recurrence),
    })

  const roundToNextQuarterHour = () => {
    // Local date setters can jump backwards during the repeated daylight-saving hour.
    const quarterHourMs = 15 * 60 * 1000
    return new Date((Math.floor(Date.now() / quarterHourMs) + 1) * quarterHourMs)
  }

  const timeOptions = useMemo(() => {
    const options: string[] = []
    for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      options.push(`${pad(hours)}:${pad(mins)}`)
    }
    return options
  }, [])

  const minDate = useMemo(
    () => toDateInputInTimeZone(new Date().toISOString(), activeEditTimeZone),
    [activeEditTimeZone],
  )

  const effectiveEndDate = useMemo(() => {
    if (eventEndDate) return eventEndDate
    return eventDate
  }, [eventEndDate, eventDate])

  const minEndDate = useMemo(() => (eventDate ? eventDate : minDate), [eventDate, minDate])

  const nearbyMeetingAdvice = useMemo(() => {
    if (!editingEvent || !eventDate || !eventStartTime || !effectiveEndDate || !eventEndTime) return []
    if (!isValidWallClockInput(eventDate, eventStartTime, activeEditTimeZone) || !isValidWallClockInput(effectiveEndDate, eventEndTime, activeEditTimeZone)) return []
    return getNearbyMeetingAdvice({
      events: internalEvents,
      start: fromWallClockInput(eventDate, eventStartTime, activeEditTimeZone, editingEvent.startTimeIso),
      end: fromWallClockInput(effectiveEndDate, eventEndTime, activeEditTimeZone, editingEvent.endTimeIso),
      timeZone: resolveTimeZone(profileTimeZone),
      editingId: editingEvent.id,
      cohortId: editingEvent.cohortId || cohortId,
      participantIds: [...selectedInvitees, ...(!isAdmin && currentUserId ? [currentUserId] : [])],
    })
  }, [activeEditTimeZone, cohortId, currentUserId, editingEvent, effectiveEndDate, eventDate, eventEndTime, eventStartTime, internalEvents, isAdmin, profileTimeZone, selectedInvitees])

  const minTimeForSelectedDate = useMemo(() => {
    if (!eventDate) return null
    if (editingEvent && editingEvent.id !== "new") return null
    if (eventDate !== minDate) return null
    const rounded = roundToNextQuarterHour()
    return toTimeInputInTimeZone(rounded.toISOString(), activeEditTimeZone)
  }, [activeEditTimeZone, editingEvent, eventDate, minDate])

  const timeZoneOptions = useMemo(
    () => getTimeZoneOptions(editingEvent?.timeZone, profileTimeZone),
    [editingEvent?.timeZone, profileTimeZone],
  )

  const filteredEndTimeOptions = useMemo(() => {
    if (!effectiveEndDate) return timeOptions
    if (!eventStartTime) return timeOptions
    if (effectiveEndDate !== eventDate) return timeOptions
    const startMinutes = timeStringToMinutes(eventStartTime)
    return timeOptions.filter((option) => timeStringToMinutes(option) > startMinutes)
  }, [effectiveEndDate, eventDate, eventStartTime, timeOptions])

  const rosterOptions = useMemo(
    () => (currentUserId ? roster.filter((u) => String(u.id) !== String(currentUserId)) : roster || []),
    [roster, currentUserId],
  )
  const filteredRosterOptions = useMemo(() => {
    const term = inviteSearch.trim().toLowerCase()
    const selectedSet = new Set(selectedInvitees.map((id) => String(id)))
    return rosterOptions.filter((u: RosterMember) => {
      if (selectedSet.has(String(u.id))) return false
      if (!term) return true
      return u.name.toLowerCase().includes(term)
    })
  }, [inviteSearch, rosterOptions, selectedInvitees])

  const displayTimeZone = resolveTimeZone(profileTimeZone)

  const formatDisplayDate = (isoString: string) =>
    new Date(isoString).toLocaleDateString("en-US", {
      timeZone: displayTimeZone,
      month: "short",
      day: "numeric",
    })

  const formatDisplayTime = (isoString: string, timeZone = displayTimeZone) =>
    new Date(isoString).toLocaleTimeString("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    })

  const formatDisplayDateTime = (isoString: string) =>
    new Date(isoString).toLocaleString("en-US", {
      timeZone: displayTimeZone,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })

  const formatDisplayDateParts = (isoString?: string, timeZone = displayTimeZone) => {
    const date = isoString ? new Date(isoString) : new Date()
    return {
      weekday: date.toLocaleDateString("en-US", { timeZone, weekday: "short" }),
      day: date.toLocaleDateString("en-US", { timeZone, day: "numeric" }),
      month: date.toLocaleDateString("en-US", { timeZone, month: "short" }),
      longDate: date.toLocaleDateString("en-US", {
        timeZone,
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
    }
  }

  const isSameDisplayDate = (startIso: string, endIso: string, timeZone = displayTimeZone) =>
    toDateInputInTimeZone(startIso, timeZone) === toDateInputInTimeZone(endIso, timeZone)

  const weekDayNames: WeekDay[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ]

  const parseLocalDateInput = (value?: string) => {
    if (!value) return null
    const [year, month, day] = value.split("-").map(Number)
    if (!year || !month || !day) return null
    return new Date(year, month - 1, day)
  }

  const addMonths = (date: Date, months: number) => {
    const next = new Date(date)
    const originalDay = next.getDate()
    next.setDate(1)
    next.setMonth(next.getMonth() + months)
    const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()
    next.setDate(Math.min(originalDay, lastDay))
    return next
  }

  const getFirstRecurrenceDate = (rule: RecurrenceRule) => {
    const start = parseLocalDateInput(rule.startDate)
    const end = rule.noEndDate ? null : parseLocalDateInput(rule.endDate)
    if (!start) return null
    if (!rule.noEndDate && !end) return null

    const interval = Math.max(1, rule.interval || 1)
    const selectedDays = new Set((rule.daysOfWeek || []).map((day) => weekDayNames.indexOf(day)).filter((day) => day >= 0))
    const isWithinEnd = (date: Date) => !end || date.getTime() <= end.getTime()

    if (rule.period === "day") {
      return isWithinEnd(start) ? start : null
    }

    if (rule.period === "week") {
      let weekStart = new Date(start)
      while (isWithinEnd(weekStart)) {
        for (let offset = 0; offset < 7; offset += 1) {
          const candidate = new Date(weekStart)
          candidate.setDate(weekStart.getDate() + offset)
          if (candidate.getTime() < start.getTime() || !isWithinEnd(candidate)) continue
          if (!selectedDays.size || selectedDays.has(candidate.getDay())) return candidate
        }
        weekStart.setDate(weekStart.getDate() + interval * 7)
      }
      return null
    }

    if (rule.period === "month") {
      let monthCursor = new Date(start)
      while (isWithinEnd(monthCursor)) {
        let dayCursor = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1)
        while (dayCursor.getMonth() === monthCursor.getMonth()) {
          if (dayCursor.getTime() >= start.getTime() && isWithinEnd(dayCursor)) {
            if (!selectedDays.size || selectedDays.has(dayCursor.getDay())) return dayCursor
          }
          dayCursor.setDate(dayCursor.getDate() + 1)
        }
        monthCursor = addMonths(monthCursor, interval)
      }
      return null
    }

    return isWithinEnd(start) ? start : null
  }

  const formatRecurrenceLabel = (recurrence?: RecurrenceRule | null) => {
    if (!recurrence) return ""
    const interval = Math.max(1, recurrence.interval || 1)
    const periodLabel = recurrence.period === "day" ? "day" : recurrence.period === "week" ? "week" : "month"
    const everyLabel = interval === 1 ? periodLabel : `${interval} ${periodLabel}${interval > 1 ? "s" : ""}`
    const days =
      recurrence.period !== "day" && recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0
        ? ` on ${recurrence.daysOfWeek
            .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
            .join(", ")}`
        : ""
    const until = recurrence.endDate ? ` until ${formatDisplayDate(recurrence.endDate)}` : ""
    return `Repeats every ${everyLabel}${days}${until}`
  }

  const mapRecurrenceFromResponse = (recurrence?: RecurrenceResponse | null): RecurrenceRule | null => {
    if (!recurrence) return null
    const anyRecurrence = recurrence as any
    return {
      interval: recurrence.interval,
      period: recurrence.period,
      daysOfWeek: recurrence.days_of_week || anyRecurrence.daysOfWeek,
      startDate: recurrence.start_date || anyRecurrence.startDate,
      endDate: recurrence.end_date || anyRecurrence.endDate || undefined,
      noEndDate: recurrence.no_end_date || anyRecurrence.noEndDate || false,
      materializedUntil: recurrence.materialized_until || anyRecurrence.materializedUntil || undefined,
    }
  }

  const mapMeetingResponse = (meeting: MeetingResponse): EventsListItem => {
    const startIso = meeting.start_time
    const endIso = meeting.end_time
    const sameDay = isSameDisplayDate(startIso, endIso)
    const displayDate = formatDisplayDate(startIso)
    const displayTime = sameDay
      ? `${formatDisplayTime(startIso)} - ${formatDisplayTime(endIso)}`
      : `${formatDisplayDateTime(startIso)} - ${formatDisplayDateTime(endIso)}`
    const invitedUserIds = Array.isArray(meeting.invited_user_ids)
      ? meeting.invited_user_ids.map((id) => String(id))
      : []
    const inviteDefaults = normalizeInviteeSelection(invitedUserIds, coerceInviteesMode(meeting.invitees_mode))
    const mappedAttachments: Attachment[] = (meeting.resources || []).map((res) => {
      const storageKey = res.storage_key || String(res.media_asset || res.id)
      const name = getAttachmentDisplayName(res.original_filename, storageKey, "Attachment")
      return {
        key: storageKey,
        name,
        mime: res.media_type,
        assetId: res.media_asset ? String(res.media_asset) : undefined,
        originalFilename: res.original_filename ?? undefined,
      }
    })

    return {
      id: String(meeting.id),
      title: meeting.title,
      description: meeting.description || undefined,
      date: displayDate,
      time: displayTime,
      status: meeting.can_join_video ? "upcoming" : meeting.status,
      attendanceTracked: Boolean(meeting.attendance_tracked),
      startTimeIso: startIso,
      endTimeIso: endIso,
      timeZone: meeting.time_zone,
      timeZoneAbbr: undefined,
      location: meeting.location || undefined,
      cohortId: meeting.cohort_id || null,
      isVirtual: meeting.is_virtual,
      isMilestone: Boolean(meeting.is_milestone),
      guestAccessEnabled: Boolean(meeting.guest_access_enabled),
      waitingRoomEnabled: Boolean(meeting.waiting_room_enabled),
      guestJoinUrl: meeting.guest_join_url || null,
      canJoinVideo: Boolean(meeting.can_join_video),
      activeParticipantCount: Number(meeting.active_participant_count || 0),
      lateJoinUntil: meeting.late_join_until || null,
      manuallyEndedAt: meeting.manually_ended_at || null,
      questionCollectionEnabled: Boolean(meeting.question_collection_enabled),
      questionSubmissionCount: Number(meeting.question_submission_count || 0),
      unreviewedQuestionSubmissionCount: Number(meeting.unreviewed_question_submission_count || 0),
      canReviewQuestions: Boolean(meeting.can_review_questions),
      virtualLink: meeting.video_url || undefined,
      transcriptionStatus: meeting.transcription_status,
      transcriptText: meeting.transcript_text || null,
      summaryText: meeting.summary_text || null,
      recordingEnabled: Boolean(meeting.recording_enabled),
      recordingStatus: meeting.recording_status || null,
      recordingAvailable: Boolean(meeting.recording_available),
      createdById: meeting.host?.id ? String(meeting.host.id) : undefined,
      attachments: mappedAttachments,
      invitedUserIds,
      inviteesMode: inviteDefaults.mode || undefined,
      recurrence: mapRecurrenceFromResponse(meeting.recurrence),
      recurrenceSeriesId: meeting.series_id || meeting.recurrence?.series_id || null,
      isException: meeting.is_exception,
      occurrenceStatus: meeting.occurrence_status || (meeting.status === "cancelled" ? "cancelled" : meeting.is_exception ? "edited" : "normal"),
      cancelled: meeting.cancelled,
    }
  }

  const defaultRecurrence = (startDate?: string): RecurrenceRule => {
    const dateToUse = startDate || eventDate || minDate
    const [year, month, day] = dateToUse.split("-").map(Number)
    const dateForWeekday =
      year && month && day
        ? new Date(year, month - 1, day)
        : new Date(dateToUse)
    const weekdayIndex = dateForWeekday.getDay()
    const defaultDay = weekDayNames[weekdayIndex]
    return {
      interval: 1,
      period: "week",
      daysOfWeek: [defaultDay],
      startDate: dateToUse,
      endDate: dateToUse,
      noEndDate: false,
    }
  }

  const validateRecurrence = (rule: RecurrenceRule | null) => {
    if (!rule) {
      setRecurrenceError("")
      return true
    }
    if (!rule.startDate) {
      setRecurrenceError("Start date is required for recurrence.")
      return false
    }
    if (rule.interval < 1) {
      setRecurrenceError("Repeat every must be at least 1.")
      return false
    }
    if (!rule.noEndDate && !rule.endDate) {
      setRecurrenceError("End date is required unless No end date is selected.")
      return false
    }
    if (!rule.noEndDate && rule.endDate && rule.endDate < rule.startDate) {
      setRecurrenceError("End date must be on or after the start date.")
      return false
    }
    if ((rule.period === "week" || rule.period === "month") && (!rule.daysOfWeek || rule.daysOfWeek.length === 0)) {
      setRecurrenceError("Select at least one day of the week.")
      return false
    }
    if (!rule.noEndDate && !getFirstRecurrenceDate(rule)) {
      setRecurrenceError("End date must include the first recurring meeting.")
      return false
    }
    setRecurrenceError("")
    return true
  }

  const toggleRecurrenceDay = (day: WeekDay) => {
    setEditingEvent((event) => {
      if (!event) return event
      const next = event.recurrence || defaultRecurrence(eventDate || minDate)
      const currentDays = new Set(next.daysOfWeek || [])
      if (currentDays.has(day)) {
        currentDays.delete(day)
      } else {
        currentDays.add(day)
      }
      const recurrence = { ...next, daysOfWeek: Array.from(currentDays) as RecurrenceRule["daysOfWeek"] }
      setRecurrenceDraft(recurrence)
      return { ...event, recurrence }
    })
  }

  const setDateTimeFromIso = (isoString?: string, timeZone?: string | null) => {
    if (!isoString) {
      setEventDate("")
      setEventStartTime("")
      return
    }
    setEventDate(toDateInputInTimeZone(isoString, timeZone))
    setEventStartTime(toTimeInputInTimeZone(isoString, timeZone))
  }

  const setEndDateTimeFromIso = (isoString?: string, timeZone?: string | null) => {
    if (!isoString) {
      setEventEndDate("")
      setEventEndTime("")
      return
    }
    setEventEndDate(toDateInputInTimeZone(isoString, timeZone))
    setEventEndTime(toTimeInputInTimeZone(isoString, timeZone))
  }

  useEffect(() => {
    const sortedEvents = sortEventsByStart(events.filter(isVisibleMeetingEvent))
    internalEventsRef.current = sortedEvents
    setInternalEvents(sortedEvents)
  }, [events])

  useEffect(() => {
    if (!deepLinkedMeetingId) return
    if (dailyCheckInOpen) {
      setSelectedEvent(null)
      return
    }
    const matchingEvent = internalEvents.find((event) => String(event.id) === String(deepLinkedMeetingId))
    if (!matchingEvent) return
    setSelectedEvent((current) => (current?.id === matchingEvent.id ? current : matchingEvent))
  }, [dailyCheckInOpen, deepLinkedMeetingId, internalEvents])

  const editingRecurringSeries = Boolean(editingEvent?.recurrenceSeriesId && editScope)
  const editingSingleOccurrence = Boolean(editingRecurringSeries && editScope === "occurrence")
  const savesRecurrenceTemplate = Boolean(editingEvent?.recurrence && !editingSingleOccurrence)
  const existingStartIsUnchanged = Boolean(
    editingEvent &&
    editingEvent.id !== "new" &&
    originalEditStartIso &&
    isValidWallClockInput(eventDate, eventStartTime, activeEditTimeZone) &&
    fromWallClockInput(eventDate, eventStartTime, activeEditTimeZone, editingEvent.startTimeIso).getTime() === new Date(originalEditStartIso).getTime(),
  )

  useLayoutEffect(() => {
    if (editingEvent?.startTimeIso) {
      const startDate = toDateInputInZone(editingEvent.startTimeIso, editingEvent.timeZone)
      const startTime = toTimeInputInTimeZone(editingEvent.startTimeIso, editingEvent.timeZone)
      setEventDate(startDate)
      setEventStartTime(startTime)
    } else {
      setEventDate("")
      setEventStartTime("")
    }
    if (editingEvent?.endTimeIso) {
      const endDate = toDateInputInZone(editingEvent.endTimeIso, editingEvent.timeZone)
      const endTime = toTimeInputInTimeZone(editingEvent.endTimeIso, editingEvent.timeZone)
      setEventEndDate(endDate)
      setEventEndTime(endTime)
    } else {
      setEventEndDate("")
      setEventEndTime("")
    }
    if (editingEvent?.startTimeIso && editingEvent?.endTimeIso) {
      const startLocal = toDateInputInZone(editingEvent.startTimeIso, editingEvent.timeZone)
      const endLocal = toDateInputInZone(editingEvent.endTimeIso, editingEvent.timeZone)
      setUseCustomRange(startLocal !== endLocal)
    } else {
      setUseCustomRange(false)
    }
    setTimeError("")
    setRecurrenceError("")
    setAttachmentError("")
  }, [editingEvent?.endTimeIso, editingEvent?.id, editingEvent?.startTimeIso, editingEvent?.timeZone])

  useEffect(() => {
    if (!minTimeForSelectedDate) return
    if (!eventStartTime || timeStringToMinutes(eventStartTime) < timeStringToMinutes(minTimeForSelectedDate)) {
      setEventStartTime(minTimeForSelectedDate)
    }
  }, [minTimeForSelectedDate, eventStartTime])

  useEffect(() => {
    if (!eventDate || useCustomRange) return
    if (!eventEndDate || eventEndDate < eventDate) {
      setEventEndDate(eventDate)
    }
  }, [eventDate, eventEndDate, useCustomRange])

  useEffect(() => {
    if (!recurrenceDraft) return
    if (!recurrenceDraft.startDate && eventDate) {
      setRecurrenceDraft({ ...recurrenceDraft, startDate: eventDate })
    }
  }, [recurrenceDraft, eventDate])

  useEffect(() => {
    if (!effectiveEndDate || !eventStartTime) return
    if (!eventEndTime) {
      const nextOption = filteredEndTimeOptions[0]
      if (nextOption) setEventEndTime(nextOption)
      return
    }
  }, [eventDate, effectiveEndDate, eventStartTime, eventEndTime, filteredEndTimeOptions])

  useEffect(() => {
    if (!eventDate || !eventStartTime || !effectiveEndDate || !eventEndTime) {
      setTimeError("")
      return
    }
    if (!isValidWallClockInput(eventDate, eventStartTime, activeEditTimeZone)) {
      setTimeError(`Start time does not exist in ${activeEditTimeZone} because of a daylight-saving transition. Choose another time.`)
      focusEventEditorTarget("event-start-time")
      return
    }
    if (!isValidWallClockInput(effectiveEndDate, eventEndTime, activeEditTimeZone)) {
      setTimeError(`End time does not exist in ${activeEditTimeZone} because of a daylight-saving transition. Choose another time.`)
      focusEventEditorTarget("event-end-time")
      return
    }
    const start = fromWallClockInput(eventDate, eventStartTime, activeEditTimeZone, editingEvent?.startTimeIso)
    const end = fromWallClockInput(effectiveEndDate, eventEndTime, activeEditTimeZone, editingEvent?.endTimeIso)
    if (start.getTime() < Date.now() && !existingStartIsUnchanged) {
      setTimeError("Start time must be in the future.")
      return
    }
    if (end.getTime() <= start.getTime()) {
      setTimeError("End time must be after start time.")
      return
    }
    if (savesRecurrenceTemplate && (isLaterRepeatedInstant(start, activeEditTimeZone) || isLaterRepeatedInstant(end, activeEditTimeZone))) {
      setTimeError(REPEATED_TIME_SERIES_ERROR)
      return
    }
    setTimeError("")
  }, [activeEditTimeZone, editingEvent?.startTimeIso, editingEvent?.endTimeIso, effectiveEndDate, eventDate, eventEndTime, eventStartTime, existingStartIsUnchanged, savesRecurrenceTemplate])

  const handleAttachmentClick = () => {
    fileInputRef.current?.click()
  }

  const handleAttachmentChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setAttachmentError("")
    setUploadingAttachment(true)
    try {
      const arrayBuffer = await file.arrayBuffer()
      const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")

      // Check for existing asset
      const lookup = await media.lookup(hashHex)
      if (lookup.found && lookup.assetId && lookup.key) {
        const displayName = getAttachmentDisplayName(
          lookup.originalFilename,
          lookup.key,
          file.name,
        )
        const attachment: Attachment = {
          key: lookup.key,
          url: undefined,
          name: displayName,
          size: lookup.size ?? file.size,
          mime: lookup.mimeType || file.type,
          assetId: lookup.assetId,
          originalFilename: lookup.originalFilename,
        }
        setEditingEvent((prev) => {
          if (!prev) return prev
          const nextAttachments = [...(prev.attachments || []), attachment]
          return { ...prev, attachments: nextAttachments }
        })
        return
      }

      const { key, url, assetId, mimeType, size, originalFilename } = await media.upload(file, {
        visibility: "private",
        folder: "event-attachments",
        hash: hashHex,
      })
      const displayName = getAttachmentDisplayName(originalFilename, key, file.name)
      const attachment: Attachment = {
        key,
        url,
        name: displayName,
        size: size ?? file.size,
        mime: mimeType || file.type,
        assetId,
        originalFilename,
      }
      setEditingEvent((prev) => {
        if (!prev) return prev
        const nextAttachments = [...(prev.attachments || []), attachment]
        return { ...prev, attachments: nextAttachments }
      })
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Failed to upload attachment."
      setAttachmentError(message)
    } finally {
      setUploadingAttachment(false)
      if (e.target) e.target.value = ""
    }
  }

  const handleRemoveAttachment = (key: string) => {
    setEditingEvent((prev) => {
      if (!prev) return prev
      return { ...prev, attachments: (prev.attachments || []).filter((att) => att.key !== key) }
    })
  }

  const handleSelect = (event: EventsListItem) => {
    setSelectedEvent(event)
    if (buildMeetingDetailsUrl) {
      onMeetingPopupUrlChange?.(event.id)
    }
    if (onSelectEvent) onSelectEvent(event)
  }

  const openEditorForEvent = (event: EventsListItem, scope: RecurrenceEditScope | null = null) => {
    setEventSaveError("")
    setCancelError("")
    setEventTitleTouched(false)
    setEventWhereTouched(false)
    setEventInviteesTouched(false)
    setEventSaveAttempted(false)
    const inviteDefaults = normalizeInviteeSelection(event.invitedUserIds, event.inviteesMode)
    const invitedUserIdsForEdit =
      event.invitedUserIds ||
      (inviteDefaults.mode === "cohort"
        ? [INVITE_COHORT]
        : inviteDefaults.mode === "mentors"
          ? [INVITE_MENTORS]
          : inviteDefaults.mode === "scholars"
            ? [INVITE_SCHOLARS]
            : inviteDefaults.mode === "custom"
              ? inviteDefaults.selected
              : [])
    const startDate = event.startTimeIso ? toDateInputInZone(event.startTimeIso, event.timeZone) : ""
    const startTime = event.startTimeIso ? toTimeInputInTimeZone(event.startTimeIso, event.timeZone) : ""
    const endDate = event.endTimeIso ? toDateInputInZone(event.endTimeIso, event.timeZone) : ""
    const endTime = event.endTimeIso ? toTimeInputInTimeZone(event.endTimeIso, event.timeZone) : ""
    const eventForEdit: EventsListItem = {
      ...event,
      inviteesMode: inviteDefaults.mode || undefined,
      invitedUserIds: invitedUserIdsForEdit,
    }
    onMeetingPopupUrlChange?.(null)
    setUseCustomRange(false)
    setSelectedEvent(null)
    setEditingEvent(eventForEdit)
    setOriginalEditStartIso(event.startTimeIso || null)
    setRecurrenceDraft(eventForEdit.recurrence ? { ...eventForEdit.recurrence } : null)
    setMobileRecurrenceExpanded(false)
    setEditScope(event.recurrence && event.recurrenceSeriesId ? scope || "occurrence" : null)
    setInviteesMode(inviteDefaults.mode)
    setSelectedInvitees(inviteDefaults.selected)
    setEditorInPersonEnabled(Boolean(event.location && event.location.trim()))
    setInitialEditSnapshot(buildEditSnapshot({
      event: eventForEdit,
      date: startDate,
      startTime,
      endDate,
      endTime,
      mode: inviteDefaults.mode,
      selected: inviteDefaults.selected,
      inPersonEnabled: Boolean(event.location && event.location.trim()),
    }))
    if (event.startTimeIso) setDateTimeFromIso(event.startTimeIso, event.timeZone)
    if (event.endTimeIso) setEndDateTimeFromIso(event.endTimeIso, event.timeZone)
    setEventEditorOpen(true)
  }

  const requestEditForEvent = (event: EventsListItem) => {
    if (event.recurrence && event.recurrenceSeriesId) {
      setSelectedEvent(event)
      setScopeChooserKind("edit")
      return
    }
    openEditorForEvent(event)
  }

  const closeDialogs = () => {
    if (selectedEvent) {
      onMeetingPopupUrlChange?.(null)
    }
    setSelectedEvent(null)
    setEditingEvent(null)
    setEventTitleTouched(false)
    setEventWhereTouched(false)
    setEventInviteesTouched(false)
    setEventSaveAttempted(false)
    setEventEditorOpen(false)
    setScopeChooserKind(null)
    setEditScope(null)
    setCancelConfirmOpen(false)
    setCancelError("")
    setEventSaveError("")
    setCancelling(false)
    setMobileRecurrenceExpanded(false)
    setSavingEvent(false)
    setEditorInPersonEnabled(false)
    setInitialEditSnapshot(null)
    setOriginalEditStartIso(null)
  }

  const updateEventState = (eventId: string, updater: (event: EventsListItem) => EventsListItem) => {
    commitEvents((prev) =>
      sortEventsByStart(prev.map((event) => (event.id === eventId ? updater(event) : event))),
    )
    setSelectedEvent((prev) => (prev && prev.id === eventId ? updater(prev) : prev))
    setEditingEvent((prev) => (prev && prev.id === eventId ? updater(prev) : prev))
  }

  const handleEndMeetingEarly = async (eventId: string) => {
    const response = await meetings.endMeeting(eventId)
    const endedAt = response?.ended_at || new Date().toISOString()
    updateEventState(eventId, (event) => ({
      ...event,
      endTimeIso: endedAt,
      status: "past",
    }))
  }

  const handleRestoreOccurrence = async (event: EventsListItem) => {
    const saved = await meetings.restoreMeeting(event.id)
    const mapped = mapMeetingResponse(saved)
    commitEvents((prev) =>
      sortEventsByStart(prev.map((item) => (item.id === mapped.id ? mapped : item))),
    )
    setSelectedEvent(mapped)
  }

  const openCreateEvent = (defaults: CreateEventDefaults = {}) => {
    setEventSaveError("")
    setCancelError("")
    setEventTitleTouched(false)
    setEventWhereTouched(false)
    setEventInviteesTouched(false)
    setEventSaveAttempted(false)
    const newEventTimeZone = resolveTimeZone(profileTimeZone)
    const rounded = roundToNextQuarterHour()
    const defaultIso = rounded.toISOString()
    const defaultDate = toDateInputInTimeZone(defaultIso, newEventTimeZone)
    const defaultTime = toTimeInputInTimeZone(defaultIso, newEventTimeZone)
    const defaultEnd = new Date(rounded.getTime() + 30 * 60 * 1000)
    const endIso = defaultEnd.toISOString()
    const defaultEndDate = toDateInputInTimeZone(endIso, newEventTimeZone)
    const defaultEndTime = toTimeInputInTimeZone(endIso, newEventTimeZone)
    const sameDay = defaultDate === defaultEndDate
    const displayDate = formatDisplayDate(defaultIso)
    const displayTime = sameDay
      ? `${formatDisplayTime(defaultIso)} - ${formatDisplayTime(endIso)}`
      : `${formatDisplayDateTime(defaultIso)} - ${formatDisplayDateTime(endIso)}`

    const defaultInviteesMode = defaults.inviteesMode || ""
    const defaultInvitees = defaultInviteesMode === "custom"
      ? (defaults.invitedUserIds || []).map(String)
      : []

    setEditingEvent({
      id: "new",
      title: defaults.title || "",
      date: displayDate,
      time: displayTime,
      status: "upcoming",
      isVirtual: false,
      isMilestone: false,
      guestAccessEnabled: false,
      waitingRoomEnabled: false,
      guestJoinUrl: null,
      questionCollectionEnabled: false,
      recordingEnabled: false,
      virtualLink: "",
      description: "",
      location: "",
      createdById: currentUserId || "admin",
      timeZone: newEventTimeZone,
      startTimeIso: defaultIso,
      endTimeIso: endIso,
      attachments: [],
      recurrence: null,
      invitedUserIds: defaultInvitees,
      inviteesMode: defaultInviteesMode || undefined,
    })
    setEventDate(defaultDate)
    setEventStartTime(defaultTime)
    setEventEndDate(defaultEndDate)
    setEventEndTime(defaultEndTime)
    setUseCustomRange(false)
    setRecurrenceDraft(null)
    setMobileRecurrenceExpanded(false)
    setEditScope(null)
    setScopeChooserKind(null)
    setInviteesMode(defaultInviteesMode)
    setSelectedInvitees(defaultInvitees)
    setEditorInPersonEnabled(false)
    setInitialEditSnapshot(null)
    setOriginalEditStartIso(null)
    setEventEditorOpen(true)
  }

  const handleCreate = () => openCreateEvent()

  useImperativeHandle(ref, () => ({
    openCreateEvent,
    openManageEvent: (eventId: string) => {
      const event = internalEventsRef.current.find((candidate) => String(candidate.id) === String(eventId))
      if (event) requestEditForEvent(event)
    },
    openEventDetails: (eventId: string) => {
      const event = internalEventsRef.current.find((candidate) => String(candidate.id) === String(eventId))
      if (event) handleSelect(event)
    },
  }))

  const handleEditTimeZoneChange = (nextTimeZone: string) => {
    if (!editingEvent || !eventDate || !eventStartTime || !effectiveEndDate || !eventEndTime) return

    const resolvedNextTimeZone = resolveTimeZone(nextTimeZone)
    if (
      !isValidWallClockInput(eventDate, eventStartTime, resolvedNextTimeZone) ||
      !isValidWallClockInput(effectiveEndDate, eventEndTime, resolvedNextTimeZone)
    ) {
      setTimeError(`Choose a valid date and time in ${resolvedNextTimeZone}.`)
      return
    }
    const currentStartIso = fromWallClockInput(eventDate, eventStartTime, resolvedNextTimeZone).toISOString()
    const currentEndIso = fromWallClockInput(effectiveEndDate, eventEndTime, resolvedNextTimeZone).toISOString()

    setTimeError("")
    setEditingEvent((current) => current
      ? {
          ...current,
          timeZone: resolvedNextTimeZone,
          startTimeIso: currentStartIso,
          endTimeIso: currentEndIso,
        }
      : current)
  }

  const hasTitle = (editingEvent?.title || "").trim().length > 0
  const currentEditSnapshot = editingEvent
    ? buildEditSnapshot({
        event: editingEvent,
        date: eventDate,
        startTime: eventStartTime,
        endDate: effectiveEndDate || "",
        endTime: eventEndTime,
        mode: inviteesMode,
        selected: selectedInvitees,
        inPersonEnabled: editorInPersonEnabled,
      })
    : null
  const hasDetectedChanges =
    !editingEvent || editingEvent.id === "new" || !initialEditSnapshot || currentEditSnapshot !== initialEditSnapshot
  const editorBusy = savingEvent || cancelling || uploadingAttachment
  const discardDisabled = !!(editorBusy || (editingEvent && editingEvent.id !== "new" && !hasDetectedChanges))
  const activeEditLocation = editorInPersonEnabled ? editingEvent?.location || "" : ""
  const titleIsInvalid = !!(editingEvent && (eventTitleTouched || eventSaveAttempted) && !hasTitle)
  const dateIsInvalid = eventSaveAttempted && !eventDate
  const startTimeIsInvalid = eventSaveAttempted && !eventStartTime
  const endDateIsInvalid = eventSaveAttempted && useCustomRange && !eventEndDate
  const endTimeIsInvalid = eventSaveAttempted && !eventEndTime
  const whereSelectionMissing = !!(editingEvent && !editingEvent.isVirtual && !editorInPersonEnabled)
  const inPersonLocationMissing = !!(editingEvent && editorInPersonEnabled && !activeEditLocation.trim())
  const whereIsInvalid = (eventWhereTouched || eventSaveAttempted) && (whereSelectionMissing || inPersonLocationMissing)
  const hasInviteeSelection = inviteesMode === "custom" ? selectedInvitees.length > 0 : inviteesMode !== ""
  const inviteesAreInvalid = !!(editingEvent && (eventInviteesTouched || eventSaveAttempted) && !hasInviteeSelection)
  const eventEditorSaveDisabled = !!(
    editorBusy ||
    (editingEvent && editingEvent.id !== "new" && !hasDetectedChanges)
  )
  const eventValidationIssues = eventSaveAttempted
    ? [
        !hasTitle ? { targetId: "event-title", label: "Event title" } : null,
        !eventDate ? { targetId: "event-date", label: "Date" } : null,
        !eventStartTime ? { targetId: "event-start-time", label: "Start time" } : null,
        useCustomRange && !eventEndDate ? { targetId: "event-end-date", label: "End date" } : null,
        !eventEndTime ? { targetId: "event-end-time", label: "End time" } : null,
        timeError ? { targetId: "event-start-time", label: "Date and time" } : null,
        whereSelectionMissing ? { targetId: "event-location-format", label: "Meeting format" } : null,
        inPersonLocationMissing ? { targetId: "event-location", label: "In-person location" } : null,
        !hasInviteeSelection ? { targetId: "event-invitees-cohort", label: "Invitees" } : null,
        recurrenceError ? { targetId: "event-recurrence-section", label: "Recurrence settings" } : null,
        attachmentError ? { targetId: "event-attachments-section", label: "Attachment" } : null,
      ].filter((issue): issue is { targetId: string; label: string } => Boolean(issue))
    : []
  const mobileValidationGuidance = eventValidationIssues.length > 0
    ? `Fix ${eventValidationIssues[0].label.toLowerCase()}${eventValidationIssues.length > 1 ? ` and ${eventValidationIssues.length - 1} more ${eventValidationIssues.length === 2 ? "field" : "fields"}` : ""} before saving.`
    : ""
  const saveDisabledBecauseUnchanged = Boolean(
    editingEvent && editingEvent.id !== "new" && !hasDetectedChanges && !editorBusy,
  )
  const eventEditorSaveGuidance = mobileValidationGuidance || (saveDisabledBecauseUnchanged ? "Make a change to save." : "")
  const eventEditorScheduleContext = (() => {
    if (!editingEvent || editingEvent.id === "new") return ""
    const startDate = parseLocalDateInput(eventDate)
    const endDate = parseLocalDateInput(effectiveEndDate)
    const startDateLabel = startDate?.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    const endDateLabel = endDate?.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    const dateLabel = startDateLabel
      ? effectiveEndDate && effectiveEndDate !== eventDate && endDateLabel
        ? `${startDateLabel} – ${endDateLabel}`
        : startDateLabel
      : "Date not set"
    const startTimeLabel = formatWallClockTimeLabel(eventStartTime)
    const endTimeLabel = formatWallClockTimeLabel(eventEndTime)
    const timeLabel = startTimeLabel && endTimeLabel ? `${startTimeLabel} – ${endTimeLabel}` : "Time not set"
    return `${dateLabel} · ${timeLabel} · ${activeEditTimeZone}`
  })()
  const eventEditorSaveLabel = editingEvent?.id === "new" ? "Save event" : "Save changes"
  const eventEditorSaveAriaLabel = editingEvent?.id === "new" ? "Save Event" : "Save changes"

  const focusEventEditorTarget = (targetId: string) => {
    const target = document.getElementById(targetId)
    if (!target) return
    if (typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center" })
    }
    target.focus({ preventScroll: true })
  }

  const updateEditingRecurrence = (updater: (rule: RecurrenceRule) => RecurrenceRule) => {
    setEditingEvent((prev) => {
      if (!prev) return prev
      const nextRule = updater(prev.recurrence || defaultRecurrence(eventDate || minDate))
      setRecurrenceDraft(nextRule)
      return { ...prev, recurrence: nextRule }
    })
  }

  const renderScopeBanner = () => {
    if (!editingEvent?.recurrenceSeriesId || !editScope) return null
    const copy = scopeBannerCopy[editScope]
    const showReset = editingEvent.occurrenceStatus === "edited" && editScope === "occurrence"
    return (
      <div className="flex items-center gap-3 rounded-[14px] border border-[var(--cohort-rule)] bg-[var(--cohort-wash)] p-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-[var(--cohort-forest)]">
          <Repeat className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-[var(--cohort-ink)]">{copy.label}</span>
          <span className="block text-xs text-[var(--cohort-ink-muted)]">{copy.helper}</span>
        </span>
        {showReset && (
          <Button
            type="button"
            variant="secondary"
            size="auto"
            className={`${popupPillButtonClass} cohort-secondary-button px-3 py-1.5 text-xs`}
            onClick={async () => {
              if (!editingEvent || savingEvent) return
              setSavingEvent(true)
              try {
                const saved = await meetings.resetMeetingToSeries(editingEvent.id)
                const mapped = mapMeetingResponse(saved)
                commitEvents((prev) => sortEventsByStart(prev.map((event) => (event.id === mapped.id ? mapped : event))))
                closeDialogs()
              } catch (err: any) {
                setTimeError(err?.response?.data?.detail || err?.message || "Failed to reset occurrence.")
              } finally {
                setSavingEvent(false)
              }
            }}
          >
            <Repeat className="h-3.5 w-3.5" />
            Reset to series
          </Button>
        )}
      </div>
    )
  }

  const renderRecurrenceEditor = () => {
    if (!editingEvent) return null
    const rule = editingEvent.recurrence
    if (editingSingleOccurrence) {
      return (
        <div className={`${eventEditorSectionClass} space-y-3`}>
          <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">Repeats</h3>
          <div className="flex gap-3 rounded-xl bg-white p-3">
            <Repeat className="mt-0.5 h-5 w-5 shrink-0 text-[var(--cohort-forest)]" />
            <div>
              <p className="text-sm font-medium text-[var(--cohort-ink)]">
                {rule ? formatRecurrenceLabel(rule) : "This meeting belongs to a recurring series."}
              </p>
              <p className="mt-1 text-xs leading-5 text-[var(--cohort-ink-muted)]">
                Recurrence can't be changed when editing a single occurrence. Switch to This and following or Entire series to edit the rule.
              </p>
            </div>
          </div>
        </div>
      )
    }

    const canDisableRepeat = !editingEvent.recurrenceSeriesId
    const activeRule = rule || defaultRecurrence(eventDate || minDate)
    const everyUnit = activeRule.period === "day" ? "days" : activeRule.period === "week" ? "weeks" : "months"
    const showDays = activeRule.period === "week" || activeRule.period === "month"

    const renderRepeatSwitch = () => (
      <button
        type="button"
        role="switch"
        aria-label="Repeats"
        aria-checked={Boolean(rule)}
        disabled={!canDisableRepeat}
        onClick={() => {
          if (!canDisableRepeat) return
          const next = rule ? null : recurrenceDraft || defaultRecurrence(eventDate || minDate)
          setRecurrenceDraft(next)
          setEditingEvent((prev) => (prev ? { ...prev, recurrence: next } : prev))
        }}
        className={`relative h-[26px] w-11 shrink-0 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--cohort-focus)] ${
          rule ? "bg-[var(--cohort-action)] hover:bg-[var(--cohort-action-hover)]" : "bg-[var(--cohort-toggle-off)] hover:brightness-95"
        } ${!canDisableRepeat ? "cursor-not-allowed opacity-60" : ""}`}
      >
        <span
          className={`absolute left-[3px] top-[3px] h-5 w-5 rounded-full bg-white transition-transform ${
            rule ? "translate-x-[18px]" : "translate-x-0"
          }`}
        />
      </button>
    )

    const renderFrequencySegment = () => (
      <div className="grid grid-cols-3 gap-0.5 rounded-[10px] bg-[rgba(53,57,71,0.08)] p-[3px]">
        {[
          { value: "day", label: "Daily" },
          { value: "week", label: "Weekly" },
          { value: "month", label: "Monthly" },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => updateEditingRecurrence((current) => ({ ...current, period: option.value as RecurrenceRule["period"] }))}
            className={`rounded-lg px-3 py-[7px] text-sm font-medium leading-none transition-colors ${
              activeRule.period === option.value
                ? "bg-white text-[var(--cohort-ink)] shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                : "text-[var(--cohort-ink-muted)] hover:text-[var(--cohort-ink)]"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    )

    const renderEndSegment = () => (
      <div className="grid grid-cols-2 gap-0.5 rounded-[10px] bg-[rgba(53,57,71,0.08)] p-[3px]">
        {[
          { value: false, label: "On date" },
          { value: true, label: "No end date" },
        ].map((option) => (
          <button
            key={option.label}
            type="button"
            onClick={() => updateEditingRecurrence((current) => ({ ...current, noEndDate: option.value }))}
            className={`rounded-lg px-3 py-[7px] text-sm font-medium leading-none transition-colors ${
              activeRule.noEndDate === option.value
                ? "bg-white text-[var(--cohort-ink)] shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                : "text-[var(--cohort-ink-muted)] hover:text-[var(--cohort-ink)]"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    )

    return (
      <div id="event-recurrence-section" tabIndex={-1} className={`${eventEditorSectionClass} space-y-3`}>
        <div className="flex items-center justify-between gap-3 border-b border-[var(--cohort-rule)] pb-2.5">
          <h3 className="text-lg">Repeats</h3>
          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--cohort-ink)]">
            <span>Repeat</span>
            {renderRepeatSwitch()}
          </div>
        </div>
        {!rule ? null : (
          <>
          {!mobileRecurrenceExpanded ? (
            <div className="sm:hidden">
              <p className="text-[15px] leading-6 text-[var(--cohort-ink-muted)]">{formatRecurrenceLabel(activeRule)}.</p>
              <button type="button" onClick={() => setMobileRecurrenceExpanded(true)} className="cohort-text-action min-h-11 text-[15px]">Change how it repeats</button>
            </div>
          ) : null}
          <div className={`${mobileRecurrenceExpanded ? "grid" : "hidden sm:grid"} grid-cols-1 gap-3 sm:grid-cols-2`}>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[var(--cohort-ink-muted)]">Frequency</Label>
              {renderFrequencySegment()}
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[var(--cohort-ink-muted)]">Repeat every</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  value={activeRule.interval || 1}
                  onChange={(e) =>
                    updateEditingRecurrence((current) => ({ ...current, interval: Math.max(1, Number(e.target.value) || 1) }))
                  }
                  className="h-10 w-16 rounded-xl border-[var(--cohort-rule)] bg-white"
                />
                <span className="text-sm text-[var(--cohort-ink-muted)]">{everyUnit}</span>
              </div>
            </div>
            {showDays && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-sm font-medium text-[var(--cohort-ink-muted)]">
                  {activeRule.period === "month" ? "On these weekdays" : "Repeat on"}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {weekDayNames.map((day) => {
                    const selected = activeRule.daysOfWeek?.includes(day) || false
                    return (
                      <button
                        key={day}
                        type="button"
                        title={day}
                        aria-pressed={selected}
                        onClick={() => toggleRecurrenceDay(day)}
                        className="cohort-choice flex h-11 w-11 flex-col items-center justify-center gap-px text-sm"
                      >
                        {day.charAt(0).toUpperCase()}{selected ? <Check className="h-[11px] w-[11px]" aria-hidden="true" /> : null}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[var(--cohort-ink-muted)]">Start date</Label>
              <Input
                type="date"
                min={minDate}
                value={activeRule.startDate || eventDate || ""}
                onChange={(e) => updateEditingRecurrence((current) => ({ ...current, startDate: e.target.value }))}
                className="h-10 rounded-xl border-[var(--cohort-rule)] bg-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[var(--cohort-ink-muted)]">Ends</Label>
              <div className="space-y-2">
                {renderEndSegment()}
                {!activeRule.noEndDate && (
                  <Input
                    type="date"
                    min={activeRule.startDate || eventDate || minDate}
                    value={activeRule.endDate || ""}
                    onChange={(e) => updateEditingRecurrence((current) => ({ ...current, endDate: e.target.value }))}
                    className="h-10 rounded-xl border-[var(--cohort-rule)] bg-white"
                  />
                )}
              </div>
            </div>
          </div>
          </>
        )}
        {recurrenceError && <p id="event-recurrence-error" className="text-xs text-[var(--cohort-clay)]">{recurrenceError}</p>}
      </div>
    )
  }

  const saveEvent = async () => {
    if (!editingEvent || saveInFlightRef.current) return
    setEventSaveAttempted(true)
    setEventTitleTouched(true)
    setEventWhereTouched(true)
    setEventInviteesTouched(true)
    setEventSaveError("")

    const hasVirtual = !!editingEvent.isVirtual
    const hasInPerson = !!editorInPersonEnabled
    const hasLocation = !!(hasInPerson && editingEvent.location && editingEvent.location.trim())
    const recurrenceIsValid = editingSingleOccurrence || validateRecurrence(editingEvent.recurrence || null)
    const firstInvalidTarget =
      !hasTitle
        ? "event-title"
        : !eventDate
          ? "event-date"
          : !eventStartTime
            ? "event-start-time"
            : !effectiveEndDate
              ? useCustomRange ? "event-end-date" : "event-date"
              : !eventEndTime
                ? "event-end-time"
                : timeError
                  ? "event-start-time"
                  : !hasInPerson && !hasVirtual
                    ? "event-location-format"
                    : hasInPerson && !hasLocation
                      ? "event-location"
                      : !hasInviteeSelection
                        ? "event-invitees-cohort"
                        : !recurrenceIsValid
                          ? "event-recurrence-section"
                          : attachmentError
                            ? "event-attachments-section"
                            : null

    if (firstInvalidTarget) {
      focusEventEditorTarget(firstInvalidTarget)
      return
    }

    if (!isValidWallClockInput(eventDate, eventStartTime, activeEditTimeZone)) {
      setTimeError(`Start time does not exist in ${activeEditTimeZone} because of a daylight-saving transition. Choose another time.`)
      return
    }
    if (!isValidWallClockInput(effectiveEndDate, eventEndTime, activeEditTimeZone)) {
      setTimeError(`End time does not exist in ${activeEditTimeZone} because of a daylight-saving transition. Choose another time.`)
      return
    }

    const localStart = fromWallClockInput(eventDate, eventStartTime, activeEditTimeZone, editingEvent.startTimeIso)
    const localEnd = fromWallClockInput(effectiveEndDate, eventEndTime, activeEditTimeZone, editingEvent.endTimeIso)
    if (localStart.getTime() < Date.now() && !existingStartIsUnchanged) {
      setTimeError("Start time must be in the future.")
      focusEventEditorTarget("event-start-time")
      return
    }
    if (localEnd.getTime() <= localStart.getTime()) {
      setTimeError("End time must be after start time.")
      focusEventEditorTarget("event-end-time")
      return
    }
    if (savesRecurrenceTemplate && (isLaterRepeatedInstant(localStart, activeEditTimeZone) || isLaterRepeatedInstant(localEnd, activeEditTimeZone))) {
      setTimeError(REPEATED_TIME_SERIES_ERROR)
      focusEventEditorTarget("event-start-time")
      return
    }

    const startIso = localStart.toISOString()
    const endIso = localEnd.toISOString()
    const sameDay = eventDate === effectiveEndDate
    const recurrenceRule = editingEvent.recurrence && !editingSingleOccurrence
      ? {
          interval: editingEvent.recurrence.interval,
          period: editingEvent.recurrence.period,
          days_of_week: editingEvent.recurrence.daysOfWeek,
          start_date: editingEvent.recurrence.startDate || eventDate,
          end_date: editingEvent.recurrence.noEndDate ? null : editingEvent.recurrence.endDate || null,
          no_end_date: Boolean(editingEvent.recurrence.noEndDate),
        }
      : null
    const inviteesModeForPayload = inviteesMode as InviteesMode
    const targetCohortId = cohortId || editingEvent.cohortId || undefined
    const inviteeIdsForPayload =
      inviteesModeForPayload === "cohort"
        ? [INVITE_COHORT]
        : inviteesModeForPayload === "mentors"
          ? [INVITE_MENTORS]
          : inviteesModeForPayload === "scholars"
            ? [INVITE_SCHOLARS]
            : selectedInvitees

    const payload = {
      title: editingEvent.title || "",
      description: editingEvent.description || "",
      location: editorInPersonEnabled ? editingEvent.location || "" : "",
      cohort_id: targetCohortId,
      start_time: startIso,
      end_time: endIso,
      start_time_local: `${eventDate}T${eventStartTime}:00`,
      end_time_local: `${effectiveEndDate}T${eventEndTime}:00`,
      time_zone: activeEditTimeZone,
      is_virtual: !!editingEvent.isVirtual,
      is_milestone: !!editingEvent.isMilestone,
      guest_access_enabled: !!(editingEvent.isVirtual && editingEvent.guestAccessEnabled),
      waiting_room_enabled: !!(editingEvent.isVirtual && editingEvent.waitingRoomEnabled),
      question_collection_enabled: !!editingEvent.questionCollectionEnabled,
      recording_enabled: !!(editingEvent.isVirtual && editingEvent.recordingEnabled),
      invitees_mode: inviteesModeForPayload,
      invited_user_ids: inviteeIdsForPayload,
      attachment_ids: normalizeAttachmentIds(editingEvent.attachments),
      recurrence: recurrenceRule || undefined,
    }

    saveInFlightRef.current = true
    setSavingEvent(true)
    setEventSaveError("")
    try {
      if (editingEvent.id === "new" && recurrenceRule) {
        const savedSeries = await meetings.createRecurrence(payload)
        const mapped = (savedSeries.meetings || []).map(mapMeetingResponse)
        commitEvents((prev) => {
          const remaining = prev.filter((e) => e.recurrenceSeriesId !== savedSeries.series_id)
          return sortEventsByStart([...mapped, ...remaining])
        })
      } else if (editingEvent.recurrenceSeriesId && editScope === "series" && recurrenceRule) {
        const savedSeries = await meetings.updateRecurrence(editingEvent.recurrenceSeriesId, payload)
        const mapped = (savedSeries.meetings || []).map(mapMeetingResponse)
        commitEvents((prev) => {
          const remaining = prev.filter((e) => e.recurrenceSeriesId !== savedSeries.series_id)
          return sortEventsByStart([...mapped, ...remaining])
        })
      } else if (editingEvent.recurrenceSeriesId && editScope === "following" && recurrenceRule) {
        const savedSeries = await meetings.splitRecurrence(editingEvent.recurrenceSeriesId, {
          ...payload,
          effective_meeting_id: editingEvent.id,
        })
        const mapped = (savedSeries.meetings || []).map(mapMeetingResponse)
        commitEvents((prev) => {
          const effectiveStart = editingEvent.startTimeIso ? new Date(editingEvent.startTimeIso).getTime() : 0
          const remaining = prev.filter((event) => {
            if (event.recurrenceSeriesId === savedSeries.series_id) return false
            if (event.recurrenceSeriesId !== editingEvent.recurrenceSeriesId) return true
            const eventStart = event.startTimeIso ? new Date(event.startTimeIso).getTime() : 0
            return eventStart < effectiveStart
          })
          return sortEventsByStart([...mapped, ...remaining])
        })
      } else {
        const saved =
          editingEvent.id === "new"
            ? await meetings.createMeeting(payload)
            : await meetings.updateMeeting(editingEvent.id, payload)

        const displayDate = formatDisplayDate(startIso)
        const displayTime = sameDay
          ? `${formatDisplayTime(startIso)} - ${formatDisplayTime(endIso)}`
          : `${formatDisplayDateTime(startIso)} - ${formatDisplayDateTime(endIso)}`
        const eventToSave = {
          ...editingEvent,
          id: String(saved.id),
          date: displayDate,
          time: displayTime,
          startTimeIso: startIso,
          endTimeIso: endIso,
          cohortId: saved.cohort_id || targetCohortId || editingEvent.cohortId || null,
          attachments: editingEvent.attachments || [],
          isVirtual: saved.is_virtual,
          guestAccessEnabled: Boolean(saved.guest_access_enabled),
          waitingRoomEnabled: Boolean(saved.waiting_room_enabled),
          guestJoinUrl: saved.guest_join_url || editingEvent.guestJoinUrl || null,
          canJoinVideo: Boolean(saved.can_join_video),
          activeParticipantCount: Number(saved.active_participant_count || 0),
          lateJoinUntil: saved.late_join_until || null,
          manuallyEndedAt: saved.manually_ended_at || null,
          questionCollectionEnabled: Boolean(saved.question_collection_enabled),
          recordingEnabled: Boolean(saved.recording_enabled),
          recordingStatus: saved.recording_status || null,
          recordingAvailable: Boolean(saved.recording_available),
          questionSubmissionCount: Number(saved.question_submission_count || 0),
          unreviewedQuestionSubmissionCount: Number(saved.unreviewed_question_submission_count || 0),
          virtualLink: saved.video_url || editingEvent.virtualLink,
          location: saved.location ?? (editorInPersonEnabled ? editingEvent.location : ""),
          recurrence: mapRecurrenceFromResponse(saved.recurrence) || editingEvent.recurrence || null,
          recurrenceSeriesId: saved.series_id || saved.recurrence?.series_id || editingEvent.recurrenceSeriesId || null,
          isException: Boolean(saved.is_exception),
          occurrenceStatus: saved.occurrence_status || (saved.status === "cancelled" ? "cancelled" : saved.is_exception ? "edited" : "normal"),
          invitedUserIds: Array.isArray(saved.invited_user_ids)
            ? saved.invited_user_ids.map((id) => String(id))
            : inviteeIdsForPayload,
          inviteesMode: inviteesModeForPayload,
        }
        commitEvents((prev) => {
          const exists = prev.some((e) => e.id === eventToSave.id)
          if (exists) {
            return sortEventsByStart(prev.map((e) => (e.id === eventToSave.id ? eventToSave : e)))
          }
          return sortEventsByStart([eventToSave, ...prev])
        })
      }
      closeDialogs()
    } catch (err: any) {
      const recurrenceMessage = err?.response?.data?.recurrence
      setEventSaveError(err?.response?.data?.detail || err?.response?.data?.video_url
        || (Array.isArray(recurrenceMessage) ? recurrenceMessage.join(" ") : recurrenceMessage)
        || err?.message || "Failed to save event.")
    } finally {
      saveInFlightRef.current = false
      setSavingEvent(false)
    }
  }

  const cancelMeeting = async (scope: RecurrenceEditScope = "occurrence", targetEvent: EventsListItem | null = editingEvent): Promise<boolean> => {
    if (!targetEvent || targetEvent.id === "new") return false
    setCancelling(true)
    setCancelError("")
    try {
      if (targetEvent.recurrenceSeriesId && scope !== "occurrence") {
        await meetings.cancelRecurrence(targetEvent.recurrenceSeriesId, {
          scope: scope === "following" ? "following" : "series",
          effective_meeting_id: scope === "following" ? targetEvent.id : undefined,
        })
        commitEvents((prev) =>
          prev.filter((event) => {
            if (event.recurrenceSeriesId !== targetEvent.recurrenceSeriesId) return true
            if (scope === "series") return false
            const eventStart = event.startTimeIso ? new Date(event.startTimeIso).getTime() : 0
            const editingStart = targetEvent.startTimeIso ? new Date(targetEvent.startTimeIso).getTime() : 0
            return eventStart < editingStart
          }),
        )
      } else {
        await meetings.deleteMeeting(targetEvent.id)
        commitEvents((prev) => prev.filter((event) => event.id !== targetEvent.id))
      }
      closeDialogs()
      return true
    } catch (err: any) {
      setCancelError(err?.response?.data?.detail || err?.message || "Failed to cancel meeting.")
      return false
    } finally {
      setCancelling(false)
    }
  }

  const openMeeting = (event: EventsListItem) => {
    const guestJoinUrl = event.guestAccessEnabled ? event.guestJoinUrl : null
    openMeetingEntry({ meetingId: event.id, guestJoinUrl, navigate, windowTarget: getMeetingUrl(event.id, guestJoinUrl) })
  }

  const scheduleEvents = internalEvents.filter((event) => {
    if (showRequiresAttendance) {
      return event.status === "past" && !event.attendanceTracked
    }
    const isPast = event.status === "past" && !event.canJoinVideo
    return showPast ? isPast : !isPast
  })
  return (
    <>
      <section className={`cohort-open-section cohort-events-section cohort-events-section--${isAdmin ? "admin" : "member"} ${!isAdmin ? "cohort-member-band cohort-member-band--sand" : ""} min-w-0 ${className || ""}`} aria-labelledby="cohort-schedule-heading">
        <p className="cohort-section-eyebrow lg:hidden">{isAdmin ? "Events" : "Schedule"}</p>
        <div data-testid="cohort-events-heading-row" className={`cohort-card-heading ${isAdmin ? "cohort-card-heading--wrap" : "cohort-events-heading--member"}`}>
          <h2 id="cohort-schedule-heading" tabIndex={-1} className={`text-[21px] text-[var(--cohort-ink)] ${isAdmin ? "lg:text-xl" : "lg:text-[22px]"}`}>
            <span className="lg:hidden">What&apos;s coming up</span><span className="hidden lg:inline">{title}</span>
          </h2>
          {isAdmin ? <div className="flex flex-wrap items-center gap-2">
            {canCreateEvent ? (
              <Button size="auto" onClick={handleCreate} className="cohort-primary-button min-h-10 shrink-0 px-[18px] py-2 text-sm">New event</Button>
            ) : null}
          </div> : canCreateEvent ? <Button size="auto" onClick={handleCreate} className="cohort-primary-button min-h-10 shrink-0 px-[18px] py-2 text-sm">New event</Button> : null}
        </div>
        {isAdmin ? (
          <div className="cohort-event-filters cohort-event-filters--admin" role="group" aria-label="Filter events">
            <button type="button" aria-pressed={!showPast && !showRequiresAttendance} onClick={() => { onTogglePast(false); onToggleRequiresAttendance?.(false) }} className="cohort-choice px-3.5 text-sm">
              {!showPast && !showRequiresAttendance ? <Check aria-hidden="true" className="h-3 w-3 shrink-0" /> : null}
              Upcoming
            </button>
            {onToggleRequiresAttendance ? <button type="button" aria-pressed={showRequiresAttendance} onClick={() => { onToggleRequiresAttendance(!showRequiresAttendance); if (!showRequiresAttendance) onTogglePast(false) }} className="cohort-choice px-3.5 text-sm">
              {showRequiresAttendance ? <Check aria-hidden="true" className="h-3 w-3 shrink-0" /> : null}
              {requiresAttendanceLabel}
            </button> : null}
            <button type="button" aria-pressed={showPast} onClick={() => { onTogglePast(!showPast); if (!showPast) onToggleRequiresAttendance?.(false) }} className="cohort-choice px-3.5 text-sm">
              {showPast ? <Check aria-hidden="true" className="h-3 w-3 shrink-0" /> : null}
              Past
            </button>
          </div>
        ) : (
          <div className="cohort-event-filters cohort-event-filters--member pt-3" role="group" aria-label="Filter events">
            <button type="button" aria-pressed={!showPast} onClick={() => onTogglePast(false)} className="cohort-choice px-3.5 text-sm sm:px-4 sm:text-[15px]">
              {!showPast ? <Check aria-hidden="true" className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" /> : null}
              Upcoming
            </button>
            <button type="button" aria-pressed={showPast} onClick={() => onTogglePast(true)} className="cohort-choice px-3.5 text-sm sm:px-4 sm:text-[15px]">
              {showPast ? <Check aria-hidden="true" className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" /> : null}
              Past
            </button>
          </div>
        )}
        {loading && <div className="py-5 text-sm text-[var(--cohort-ink-muted)]">{loadingLabel}</div>}
        {error && !loading && <div className="my-3 rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] p-3 text-sm text-[var(--cohort-clay)]">{error}</div>}
        {!loading && !error && internalEvents.length === 0 ? (
          <div className="py-5 text-sm leading-6 text-[var(--cohort-ink-muted)]"><p className="font-medium text-[var(--cohort-ink)]">Nothing scheduled yet.</p><p>{emptyLabel}</p></div>
        ) : null}
        {!loading && !error && scheduleEvents.length > 0 ? (
        <div className="cohort-bounded-list cohort-events-list" role="region" aria-label={`${title} list`}>
        {scheduleEvents.map((event) => {
                const relationship = continuityRelationships.find(row => row.is_current && relationshipEventsFor([event], row.cohort_id, String(row.mentor.id), row.mentee.id).length > 0)
                const openTopicCount = relationship?.items.filter(item => item.kind === "topic" && item.status === "open" && !item.is_removed).length || 0
                const canJoinRow = Boolean(event.isVirtual && event.canJoinVideo)
                const adminIsNotInvited = Boolean(
                  isAdmin && currentUserId !== undefined
                  && !isCurrentUserMeetingParticipant(event, currentUserId),
                )
                const isJoinableOverrun =
                  canJoinRow && event.endTimeIso ? new Date(event.endTimeIso).getTime() <= Date.now() : false
                const isPast = event.status === "past" && !canJoinRow
                const isNextGathering = !showPast && !showRequiresAttendance && event.id === scheduleEvents[0]?.id
                const startIso = event.startTimeIso
                const endIso = event.endTimeIso
            const eventTimeZone = resolveMeetingTimeZone(event.timeZone, profileTimeZone)
            const zoneLabel = startIso ? meetingTimeZoneLabel(new Date(startIso), eventTimeZone) : eventTimeZone
            const dateParts = startIso ? formatDisplayDateParts(startIso, eventTimeZone) : null
            const popupState = getPopupState(event)
            const isLive = popupState === "live" || popupState === "rejoin"
            const startLabel = startIso ? formatDisplayTime(startIso, eventTimeZone) : event.time || "Time to be shared"
            const endLabel = endIso ? formatDisplayTime(endIso, eventTimeZone) : ""
            const eventLocation = event.isVirtual && !event.location?.trim() ? "Online" : event.location?.trim() || "Location to be shared"
            const invitedCount = event.invitedUserIds?.length || roster.length
            const memberTimeRange = startIso && endIso && isSameDisplayDate(startIso, endIso, eventTimeZone)
              ? `${startLabel} – ${endLabel}`
              : startLabel
            const mobileMeta = isLive && !isAdmin
              ? `In progress${endLabel ? ` · until ${endLabel} ${zoneLabel}` : ""}`
              : `${startLabel} ${zoneLabel} · ${eventLocation}`
            const desktopMeta = isLive && !isAdmin
              ? `In progress${endLabel ? ` · until ${endLabel} ${zoneLabel}` : ""} · ${eventLocation}`
              : isAdmin
                ? `${startLabel} ${zoneLabel} · ${eventLocation}${invitedCount ? ` · ${invitedCount} invited` : ""}`
                : `${memberTimeRange} ${zoneLabel} · ${eventLocation}`
            return (
              <div
                key={event.id}
                role="group"
                aria-label={`${event.title} meeting row`}
                className={`cohort-ruled-row cohort-event-row ${isPast ? "text-[var(--cohort-ink-muted)]" : ""}`}
                onClick={(clickEvent) => {
                  const clickTarget = clickEvent.target as HTMLElement
                  if (clickTarget.closest("button, a")) return
                  handleSelect(event)
                }}
              >
                {dateParts ? (
                  <div className={`cohort-event-row__date ${isNextGathering ? "text-[var(--cohort-orange-ink)]" : "text-[var(--cohort-ink-muted)]"}`}>
                    <time dateTime={startIso} className="cohort-event-row__date-mobile">
                      {isLive ? <><span className="cohort-event-row__live-dot" aria-hidden="true" />Now</> : `${dateParts.weekday} ${dateParts.day} ${dateParts.month}`}
                    </time>
                    <time dateTime={startIso} className="cohort-event-row__date-desktop">
                      <span className="cohort-event-row__weekday">{isLive ? <><span className="cohort-event-row__live-dot" aria-hidden="true" />NOW</> : dateParts.weekday}</span>
                      <span className="cohort-event-row__day">{isLive ? "Today" : `${dateParts.day} ${dateParts.month}`}</span>
                    </time>
                  </div>
                ) : (
                  <div className={`cohort-event-row__date text-sm font-semibold ${isNextGathering ? "text-[var(--cohort-orange-ink)]" : "text-[var(--cohort-ink-muted)]"}`}>
                    <time>{event.date}</time>
                  </div>
                )}
                <div className="cohort-event-row__body flex min-w-0 items-center gap-3">
                  <button type="button" aria-label="View event details" onClick={() => handleSelect(event)} className="cohort-event-row__details min-w-0 flex-1 text-left">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="block break-words text-base font-medium text-[var(--cohort-ink)] sm:text-[17px]">{event.title}</span>
                      {adminIsNotInvited ? (
                        <span className="inline-flex shrink-0 items-center rounded-full border border-[var(--cohort-rule-strong)] bg-white px-2 py-0.5 text-[11px] font-semibold text-[var(--cohort-ink-muted)]">
                          Not invited
                        </span>
                      ) : null}
                    </span>
                    {event.isVirtual ? <span className="sr-only">Virtual</span> : null}
                    {openTopicCount ? <span className="mt-1 block text-xs text-[var(--cohort-forest)]">{openTopicCount} open {openTopicCount === 1 ? "topic" : "topics"}</span> : null}
                    {isJoinableOverrun ? <span className="block text-xs font-medium text-[var(--cohort-orange-ink)]">Still active</span> : null}
                    <span data-testid={`cohort-event-mobile-meta-${event.id}`} className={`mt-0.5 block break-words text-sm leading-5 sm:hidden ${isLive && !isAdmin ? "text-[var(--cohort-orange-ink)]" : "text-[var(--cohort-ink-muted)]"}`}>{mobileMeta}</span>
                    <span className={`mt-0.5 hidden break-words text-sm leading-5 sm:block ${isLive && !isAdmin ? "text-[var(--cohort-orange-ink)]" : "text-[var(--cohort-ink-muted)]"}`}>{desktopMeta}</span>
                    {event.recurrence && (
                      <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--cohort-ink-muted)]"><span className="inline-flex items-center gap-1"><Repeat className="h-3.5 w-3.5" />{formatRecurrenceLabel(event.recurrence)}</span>{event.occurrenceStatus === "edited" ? <span>Edited occurrence</span> : null}</span>
                    )}
                    {isAdmin && isPast && !event.attendanceTracked ? <span className="mt-1 flex items-center gap-1.5 text-sm text-[var(--cohort-clay)]"><TriangleAlert aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />Attendance not yet recorded</span> : null}
                  </button>
                  {!isAdmin && canJoinRow ? (
                    <Button
                      className="cohort-primary-button shrink-0 px-[18px] py-2 text-[15px] sm:hidden"
                      onClick={(clickEvent) => {
                        clickEvent.stopPropagation()
                        openMeeting(event)
                      }}
                      size="auto"
                    >
                      {isJoinableOverrun ? "Rejoin" : "Join"}
                    </Button>
                  ) : null}
                </div>
                <div data-testid={`cohort-event-actions-${event.id}`} className="cohort-event-row__actions hidden flex-wrap items-center justify-end gap-2.5 sm:flex">
                {isAdmin ? (
                  <>
                    {isPast && !event.attendanceTracked ? <Button type="button" size="auto" onClick={() => handleSelect(event)} className="cohort-primary-button min-h-[42px] px-[18px] text-[15px]">Record attendance</Button> : null}
                    <Button type="button" variant="secondary" size="auto" onClick={() => requestEditForEvent(event)} className="cohort-secondary-button min-h-[42px] px-[18px] text-[15px]">Manage</Button>
                  </>
                ) : canJoinRow ? (
                    <Button
                      className="cohort-primary-button shrink-0 px-[22px] py-2 text-[15px]"
                      onClick={(e) => {
                        e.stopPropagation()
                        openMeeting(event)
                      }}
                      size="auto"
                    >
                      {isJoinableOverrun ? "Rejoin" : "Join"}
                    </Button>
                ) : (
                    <Button type="button" variant="secondary" size="auto" onClick={() => handleSelect(event)} className="cohort-secondary-button min-h-11 px-5 text-[15px]">Details</Button>
                )}
                </div>
              </div>
            )
        })}
        </div>
        ) : null}
        {!loading && !error && internalEvents.length > 0 && scheduleEvents.length === 0 ? (
              <div className="py-5 text-sm text-[var(--cohort-ink-muted)]">
                {showRequiresAttendance
                  ? "There are no events/meetings that need attendance tracking."
                  : "There are no past events/meetings."}
              </div>
        ) : null}
      </section>
      {selectedEvent && (
        <EventDetailsDialog
          viewerTimeZone={profileTimeZone}
          event={selectedEvent}
          onClose={closeDialogs}
          onJoinMeeting={openMeeting}
          isAdmin={isAdmin}
          currentUserId={currentUserId}
          roster={roster}
          roleLabels={roleLabels}
          onEndMeeting={handleEndMeetingEarly}
          onAttendanceUpdated={(meetingId, attendanceTracked) => {
            updateEventState(meetingId, (event) => ({
              ...event,
              attendanceTracked,
            }))
          }}
          onQuestionCountsUpdated={(meetingId, totalCount, unreviewedCount) => {
            updateEventState(meetingId, (event) => ({
              ...event,
              questionSubmissionCount: totalCount,
              unreviewedQuestionSubmissionCount: unreviewedCount,
            }))
          }}
          onEdit={requestEditForEvent}
          onRestoreOccurrence={handleRestoreOccurrence}
          buildMeetingDetailsUrl={buildMeetingDetailsUrl}
          continuityRelationships={continuityRelationships}
          onOpenContinuity={onOpenContinuity}
        />
      )}

      {eventEditorOpen && editingEvent && (
        <AnimatedDialog
          onClose={closeDialogs}
          closeDisabled={editorBusy}
          hideCloseButton
          className={`${popupShellClass} cohort-dialog cohort-event-editor`}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{editingEvent.id === "new" ? "New Event" : "Edit Event"}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">
            {editingEvent.id === "new"
              ? "Create a meeting by choosing its date, time, timezone, location, invitees, and options."
              : `Update ${editingEvent.title || "this meeting"}'s date, time, timezone, location, invitees, and options.`}
          </DialogDescription>
          <button
            type="button"
            aria-label="Close"
            onClick={closeDialogs}
            disabled={editorBusy}
            className="cohort-icon-button cohort-event-editor__close absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center text-[var(--cohort-ink)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X className="h-[19px] w-[19px]" />
          </button>

          <div data-testid="event-editor-header" className={`${eventEditorHeroClass} cohort-event-editor__header px-5 pb-[18px] pt-[22px] sm:px-7`}>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-[var(--cohort-ink-muted)]">{editingEvent.id === "new" ? "New event" : `Editing${editingEvent.recurrence ? ` · ${formatRecurrenceLabel(editingEvent.recurrence)}` : ""}`}</p>
              <h2 className="mt-1 pr-10 text-2xl tracking-[-0.01em]">{editingEvent.id === "new" ? "Create an event" : editingEvent.title || "Untitled event"}</h2>
              {eventEditorScheduleContext ? (
                <p data-testid="event-editor-schedule-context" className="mt-2 pr-10 text-[13px] leading-5 text-[var(--cohort-ink-muted)]">
                  {eventEditorScheduleContext}
                </p>
              ) : null}
            </div>
          </div>

          <div data-testid="event-editor-body" className="cohort-event-editor__body flex min-h-0 flex-1 flex-col overflow-y-auto bg-[var(--cohort-paper)] px-5 py-1 sm:px-7">
            <fieldset disabled={editorBusy} className="contents">
            {renderScopeBanner()}
            {eventValidationIssues.length > 0 ? (
              <div
                data-testid="event-editor-validation-summary"
                role="alert"
                className="mt-4 rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] px-3.5 py-3 text-sm text-[var(--cohort-clay)]"
              >
                <p className="font-semibold">
                  Please fix {eventValidationIssues.length} {eventValidationIssues.length === 1 ? "field" : "fields"} before saving.
                </p>
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                  {eventValidationIssues.map((issue) => (
                    <button
                      key={`${issue.targetId}-${issue.label}`}
                      type="button"
                      className="min-h-8 text-left font-medium underline decoration-current underline-offset-2"
                      onClick={() => focusEventEditorTarget(issue.targetId)}
                    >
                      {issue.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="py-5">
              <Label htmlFor="event-title" className="text-sm font-medium">
                Event title <span aria-hidden="true" className="text-[var(--cohort-clay)]">*</span>
              </Label>
              <Input id="event-title" required aria-label="Event Title" aria-invalid={titleIsInvalid} aria-describedby={titleIsInvalid ? "event-title-error" : undefined} placeholder="e.g., Weekly check-in" value={editingEvent.title || ""} onBlur={() => setEventTitleTouched(true)} onChange={(event) => setEditingEvent((previous) => previous ? { ...previous, title: event.target.value } : previous)} className={`mt-2 min-h-12 rounded-xl bg-white text-base ${titleIsInvalid ? "border-[var(--cohort-clay)]" : "border-[var(--cohort-rule)]"}`} />
              {titleIsInvalid ? <p id="event-title-error" className="mt-2 text-sm text-[var(--cohort-clay)]">Give this event a title.</p> : null}
            </div>

            <div className={`${eventEditorSectionClass} space-y-3`} data-testid="event-editor-when-section">
              <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">When</h3>
              <div className="grid gap-3 sm:grid-cols-3 sm:gap-3.5">
                <div>
                  <Label htmlFor="event-date" className="text-[13px] text-[var(--cohort-ink-muted)]">Date <span aria-hidden="true" className="text-[var(--cohort-clay)]">*</span></Label>
                  <Input id="event-date" required aria-label="Event date" aria-invalid={dateIsInvalid} aria-describedby={dateIsInvalid ? "event-date-error" : undefined} type="date" min={minDate} value={eventDate} onChange={(event) => setEventDate(event.target.value)} className={`mt-1.5 min-h-12 rounded-xl bg-white px-3 text-base ${dateIsInvalid ? "border-[var(--cohort-clay)]" : ""}`} />
                  {dateIsInvalid ? <p id="event-date-error" className="mt-1.5 text-xs text-[var(--cohort-clay)]">Choose a date.</p> : null}
                </div>
                <div>
                  <Label htmlFor="event-start-time" className="text-[13px] text-[var(--cohort-ink-muted)]">Starts <span aria-hidden="true" className="text-[var(--cohort-clay)]">*</span></Label>
                  <Input id="event-start-time" required aria-label="Start Time" aria-invalid={startTimeIsInvalid || Boolean(eventSaveAttempted && timeError)} aria-describedby={startTimeIsInvalid ? "event-start-time-error" : eventSaveAttempted && timeError ? "event-time-error" : undefined} type="time" value={eventStartTime} onChange={(event) => setEventStartTime(event.target.value)} className={`mt-1.5 min-h-12 rounded-xl bg-white px-3 text-base ${startTimeIsInvalid || (eventSaveAttempted && timeError) ? "border-[var(--cohort-clay)]" : ""}`} />
                  {startTimeIsInvalid ? <p id="event-start-time-error" className="mt-1.5 text-xs text-[var(--cohort-clay)]">Choose a start time.</p> : null}
                </div>
                <div>
                  <Label htmlFor="event-end-time" className="text-[13px] text-[var(--cohort-ink-muted)]">Ends <span aria-hidden="true" className="text-[var(--cohort-clay)]">*</span></Label>
                  <Input id="event-end-time" required aria-label="End Time" aria-invalid={endTimeIsInvalid || Boolean(eventSaveAttempted && timeError)} aria-describedby={endTimeIsInvalid ? "event-end-time-error" : eventSaveAttempted && timeError ? "event-time-error" : undefined} type="time" value={eventEndTime} onChange={(event) => setEventEndTime(event.target.value)} className={`mt-1.5 min-h-12 rounded-xl bg-white px-3 text-base ${endTimeIsInvalid || (eventSaveAttempted && timeError) ? "border-[var(--cohort-clay)]" : ""}`} />
                  {endTimeIsInvalid ? <p id="event-end-time-error" className="mt-1.5 text-xs text-[var(--cohort-clay)]">Choose an end time.</p> : null}
                </div>
              </div>
              {useCustomRange ? <div><Label htmlFor="event-end-date" className="text-[13px] text-[var(--cohort-ink-muted)]">End date <span aria-hidden="true" className="text-[var(--cohort-clay)]">*</span></Label><Input id="event-end-date" required aria-label="End date" aria-invalid={endDateIsInvalid} aria-describedby={endDateIsInvalid ? "event-end-date-error" : undefined} type="date" min={minEndDate} value={eventEndDate} onChange={(event) => setEventEndDate(event.target.value)} className={`mt-1.5 min-h-12 rounded-xl bg-white px-3 text-base ${endDateIsInvalid ? "border-[var(--cohort-clay)]" : ""}`} />{endDateIsInvalid ? <p id="event-end-date-error" className="mt-1.5 text-xs text-[var(--cohort-clay)]">Choose an end date.</p> : null}</div> : null}
              <div><Label htmlFor="event-timezone" className="text-[13px] text-[var(--cohort-ink-muted)]">Timezone</Label><select id="event-timezone" value={activeEditTimeZone} onChange={(event) => handleEditTimeZoneChange(event.target.value)} className="mt-1.5 min-h-12 w-full rounded-xl border border-[var(--cohort-rule)] bg-white px-3.5 text-base text-[var(--cohort-ink)]">{timeZoneOptions.map((timeZone) => <option key={timeZone} value={timeZone}>{timeZone}</option>)}</select></div>
              {nearbyMeetingAdvice.length > 0 ? <div role="status" className="rounded-xl border border-[var(--cohort-rule)] bg-[var(--cohort-ochre-soft)] p-3.5 text-sm">
                <p className="font-medium">Nearby cohort meetings · {resolveTimeZone(profileTimeZone)}</p>
                <ul className="mt-2 space-y-2">{nearbyMeetingAdvice.map(advice => <li key={advice.event.id} className={advice.overlap ? "text-[var(--cohort-clay)]" : "text-[var(--cohort-ink-muted)]"}>
                  <span>{advice.message}</span><span className="ml-1 text-xs">({formatDisplayTime(advice.event.startTimeIso!)}–{formatDisplayTime(advice.event.endTimeIso!)})</span>
                </li>)}</ul>
                <p className="mt-2 text-xs text-[var(--cohort-ink-muted)]">Check that everyone has time to move between calls. Saving keeps the times you choose.</p>
              </div> : null}
              <button type="button" className="cohort-text-action min-h-11 text-[15px]" onClick={() => { if (useCustomRange) { setUseCustomRange(false); setEventEndDate(eventDate) } else { if (!eventEndDate) setEventEndDate(eventDate); setUseCustomRange(true) } }}>{useCustomRange ? "This event happens on one day" : "Runs across more than one day"}</button>
              {timeError ? <p id="event-time-error" className="text-sm text-[var(--cohort-clay)]">{timeError}</p> : null}
            </div>
            <div id="event-location-section" tabIndex={-1} className={`${eventEditorSectionClass} space-y-3 ${whereIsInvalid ? "rounded-xl bg-[var(--cohort-clay-soft)] px-3" : ""}`}>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">Where <span aria-hidden="true" className="text-[var(--cohort-clay)]">*</span></h3>
              <div className="flex gap-2.5" role="group" aria-label="Meeting format, required" aria-describedby={whereSelectionMissing && (eventWhereTouched || eventSaveAttempted) ? "event-location-format-error" : undefined}>
                <button
                  id="event-location-format"
                  type="button"
                  aria-pressed={!!editingEvent.isVirtual}
                  onClick={() => {
                    setEventWhereTouched(true)
                    setEditingEvent((prev) =>
                      prev
                        ? {
                            ...prev,
                            isVirtual: !prev.isVirtual,
                            virtualLink: !prev.isVirtual ? prev.virtualLink : "",
                            guestAccessEnabled: !prev.isVirtual ? prev.guestAccessEnabled : false,
                            waitingRoomEnabled: !prev.isVirtual ? prev.waitingRoomEnabled : false,
                            recordingEnabled: !prev.isVirtual ? prev.recordingEnabled : false,
                          }
                        : prev,
                    )
                  }}
                  className="cohort-choice inline-flex min-h-[46px] items-center gap-2 px-[18px] text-[15px]"
                >
                  {editingEvent.isVirtual ? <Check className="h-4 w-4" aria-hidden="true" /> : null}Online
                </button>
                <button
                  type="button"
                  aria-pressed={editorInPersonEnabled}
                  onClick={() => {
                    setEventWhereTouched(true)
                    setEditorInPersonEnabled((value) => !value)
                  }}
                  className="cohort-choice inline-flex min-h-[46px] items-center gap-2 px-[18px] text-[15px]"
                >
                  {editorInPersonEnabled ? <Check className="h-4 w-4" aria-hidden="true" /> : null}In person
                </button>
              </div>
              {editorInPersonEnabled && (
                <div>
                  <Label htmlFor="event-location" className="text-[13px] text-[var(--cohort-ink-muted)]">Location <span aria-hidden="true" className="text-[var(--cohort-clay)]">*</span></Label>
                  <Input
                  id="event-location"
                  required
                  placeholder="e.g., Camp Serenity"
                  value={editingEvent.location || ""}
                  aria-invalid={whereIsInvalid && inPersonLocationMissing}
                  aria-describedby={whereIsInvalid && inPersonLocationMissing ? "event-location-error" : undefined}
                  onChange={(e) => {
                    const value = e.target.value
                    setEditingEvent((prev) => (prev ? { ...prev, location: value } : prev))
                  }}
                  className={`mt-1.5 rounded-xl bg-white ${
                    whereIsInvalid && inPersonLocationMissing
                      ? "border-[var(--cohort-clay)] focus:border-[var(--cohort-clay)] focus:ring-1 focus:ring-[var(--cohort-clay-rule)]"
                      : ""
                  }`}
                />
                </div>
              )}
              {(eventWhereTouched || eventSaveAttempted) && whereSelectionMissing && (
                <p id="event-location-format-error" className="text-xs text-[var(--cohort-clay)]">Choose online, in person, or both.</p>
              )}
              {(eventWhereTouched || eventSaveAttempted) && inPersonLocationMissing && (
                <p id="event-location-error" className="text-xs text-[var(--cohort-clay)]">
                  Add a location for in-person meetings.
                </p>
              )}
            </div>

            <div id="event-invitees-section" tabIndex={-1} className={`${eventEditorSectionClass} space-y-3 ${inviteesAreInvalid ? "rounded-xl bg-[var(--cohort-clay-soft)] px-3" : ""}`}>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">Who&apos;s invited <span aria-hidden="true" className="text-[var(--cohort-clay)]">*</span></h3>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Event invitees, required" aria-describedby={inviteesAreInvalid ? "event-invitees-error" : undefined}>
                {[
                  { value: "cohort" as InviteesMode, label: "Everyone", count: rosterOptions.length },
                  { value: "scholars" as InviteesMode, label: getInviteeBucketLabel("scholars", roleLabels), count: rosterOptions.filter((user) => rosterMemberHasRole(user, "mentee")).length },
                  { value: "mentors" as InviteesMode, label: getInviteeBucketLabel("mentors", roleLabels), count: rosterOptions.filter((user) => rosterMemberHasRole(user, "mentor")).length },
                  { value: "custom" as InviteesMode, label: "Pick people", count: null },
                ].map((option) => (
                  <button
                    key={option.value}
                    id={option.value === "cohort" ? "event-invitees-cohort" : undefined}
                    type="button"
                    aria-pressed={inviteesMode === option.value}
                    onClick={() => {
                      setEventInviteesTouched(true)
                      setInviteesMode(option.value)
                      if (option.value !== "custom") {
                        setSelectedInvitees([])
                      }
                    }}
                    className="cohort-choice inline-flex min-h-[46px] items-center gap-2 px-4 text-[15px]"
                  >
                    {inviteesMode === option.value ? <Check className="h-4 w-4" aria-hidden="true" /> : null}{option.label}{option.count !== null ? ` · ${option.count}` : ""}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center">
                  {rosterOptions.slice(0, 5).map((user, index) => (
                    <span key={String(user.id)} className={index > 0 ? "-ml-2" : ""}>
                      <AvatarBubble name={user.name} seed={user.id} size="sm" avatarUrl={getRosterAvatarUrl(user)} />
                    </span>
                  ))}
                </span>
                <span className="text-xs text-[var(--cohort-ink-muted)]">
                  {inviteesMode === "custom"
                    ? `${selectedInvitees.length} selected`
                    : inviteesMode === "mentors"
                      ? getInviteeBucketLabel("mentors", roleLabels)
                      : inviteesMode === "scholars"
                        ? getInviteeBucketLabel("scholars", roleLabels)
                        : inviteesMode === "cohort"
                          ? `${rosterOptions.length} people`
                          : "Choose invitees"}
                </span>
              </div>
              {inviteesMode === "custom" && (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--cohort-ink-muted)]" />
                    <Input
                      placeholder="Search cohort members..."
                      value={inviteSearch}
                      onChange={(e) => setInviteSearch(e.target.value)}
                      className="rounded-xl bg-white pl-9"
                    />
                  </div>
                  {filteredRosterOptions.length > 0 && (
                    <div className="cohort-bounded-list cohort-bounded-list--compact max-h-32 rounded-xl border border-[var(--cohort-rule)] bg-white" role="region" aria-label="Matching event invitees">
                      {filteredRosterOptions.map((user: RosterMember) => (
                        <button
                          key={String(user.id)}
                          type="button"
                          className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-[var(--cohort-wash)]"
                          onClick={() => {
                            setSelectedInvitees([...selectedInvitees, String(user.id)])
                            setInviteSearch("")
                          }}
                        >
                          <span>{user.name}</span>
                          {user.role && <span className="text-xs text-[var(--cohort-ink-muted)]">{user.role}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="cohort-bounded-list cohort-bounded-list--compact flex flex-wrap gap-2" role="region" aria-label="Selected event invitees">
                    {selectedInvitees.map((id) => {
                      const user = rosterOptions.find((u: RosterMember) => String(u.id) === String(id))
                      const label = user?.name || id
                      return (
                        <Badge key={id} variant="secondary" className="gap-1 rounded-lg border border-[var(--cohort-rule)] bg-white pr-1 text-[var(--cohort-ink)]">
                          {label}
                          <button
                            type="button"
                            onClick={() => setSelectedInvitees(selectedInvitees.filter((value) => value !== id))}
                            className="ml-1 rounded-md p-0.5 hover:bg-[var(--cohort-wash)]"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      )
                    })}
                  </div>
                  {rosterOptions.length === 0 && (
                    <p className="text-xs text-[var(--cohort-ink-muted)]">No roster available to select individuals.</p>
                  )}
                </div>
              )}
              {inviteesAreInvalid && (
                <p id="event-invitees-error" className="text-xs text-[var(--cohort-clay)]">
                  {inviteesMode === "custom" ? "Choose at least one person to invite." : "Choose who should be invited."}
                </p>
              )}
            </div>

            <div className={`${eventEditorSectionClass} space-y-2`}>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">Description</h3>
              <Textarea
                placeholder="Add event details, agenda, or notes..."
                value={editingEvent.description || ""}
                onChange={(e) => {
                  const value = e.target.value
                  setEditingEvent((prev) => (prev ? { ...prev, description: value } : prev))
                }}
                className="min-h-[88px] rounded-xl bg-white"
              />
            </div>

            {renderRecurrenceEditor()}

            <div className={`${eventEditorSectionClass} space-y-3`}>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">Options</h3>
              {[
                {
                  id: "milestone",
                  checked: Boolean(editingEvent.isMilestone),
                  label: "Program milestone",
                  description: "Marks the calendar with a milestone badge.",
                  onChange: (checked: boolean) =>
                    setEditingEvent((prev) => (prev ? { ...prev, isMilestone: checked } : prev)),
                  action: null,
                },
                ...(editingEvent.isVirtual
                  ? [
                      {
                        id: "guest-access",
                        checked: Boolean(editingEvent.guestAccessEnabled),
                        label: "Allow guest access",
                        description: "Let people without an account use the copied meeting link.",
                        onChange: (checked: boolean) =>
                          setEditingEvent((prev) => (prev ? { ...prev, guestAccessEnabled: checked } : prev)),
                        action: null,
                      },
                      {
                        id: "waiting-room",
                        checked: Boolean(editingEvent.waitingRoomEnabled),
                        label: "Waiting room",
                        description: "Hold participants until a host admits them.",
                        onChange: (checked: boolean) =>
                          setEditingEvent((prev) => (prev ? { ...prev, waitingRoomEnabled: checked } : prev)),
                        action: null,
                      },
                      {
                        id: "record-call",
                        checked: Boolean(editingEvent.recordingEnabled),
                        label: "Record call",
                        description: "Save a private recording after the meeting ends.",
                        onChange: (checked: boolean) =>
                          setEditingEvent((prev) => (prev ? { ...prev, recordingEnabled: checked } : prev)),
                        action: null,
                      },
                    ]
                  : []),
                {
                  id: "question-collection",
                  checked: Boolean(editingEvent.questionCollectionEnabled),
                  label: "Collect questions in advance",
                  description: "Members can send a private note before we meet.",
                  onChange: (checked: boolean) =>
                    setEditingEvent((prev) => (prev ? { ...prev, questionCollectionEnabled: checked } : prev)),
                  action: null,
                },
              ].map((option) => (
                <div key={option.id} className="grid grid-cols-[1fr_auto] items-center gap-3">
                  <div>
                    <span className="block text-sm font-medium text-[var(--cohort-ink)]">{option.label}</span>
                    <span className="block text-xs text-[var(--cohort-ink-muted)]">{option.description}</span>
                  </div>
                  <button
                    id={option.id}
                    type="button"
                    aria-label={option.label}
                    onClick={() => option.onChange(!option.checked)}
                    role="switch"
                    aria-checked={option.checked}
                    className={`relative h-[26px] w-11 shrink-0 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--cohort-focus)] ${option.checked ? "bg-[var(--cohort-action)] hover:bg-[var(--cohort-action-hover)]" : "bg-[var(--cohort-toggle-off)] hover:brightness-95"}`}
                  >
                    <span className={`absolute left-[3px] top-[3px] h-5 w-5 rounded-full bg-white transition-transform ${option.checked ? "translate-x-[18px]" : ""}`} />
                  </button>
                </div>
              ))}
            </div>

            <div id="event-attachments-section" tabIndex={-1} className={`${eventEditorSectionClass} space-y-3`}>
              <h3 className="border-b border-[var(--cohort-rule)] pb-2.5 text-lg">Attachments</h3>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleAttachmentChange} />
              <div className="cohort-bounded-list cohort-bounded-list--compact flex flex-wrap gap-2" role="region" aria-label="Event attachments">
                {(editingEvent.attachments || []).map((attachment) => {
                  const label = getAttachmentDisplayName(attachment.originalFilename, attachment.key, attachment.name)
                  const isVideo = (attachment.mime || "").toLowerCase().includes("video")
                  return (
                    <span key={attachment.key} className="inline-flex min-h-11 max-w-full items-center gap-2 rounded-xl border border-[var(--cohort-rule)] bg-white py-1.5 pl-3 pr-1.5 text-sm text-[var(--cohort-ink)]">
                      {isVideo ? <Film className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                      <span className="max-w-[12rem] truncate" title={label}>{label}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(attachment.key)}
                        className="cohort-icon-button flex h-11 w-11 items-center justify-center"
                        aria-label={`Remove ${label}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )
                })}
                <button
                  type="button"
                  onClick={handleAttachmentClick}
                  disabled={uploadingAttachment}
                  className="cohort-secondary-button inline-flex items-center gap-2 border-dashed px-4 text-[15px] disabled:opacity-100"
                >
                  {uploadingAttachment ? <Upload className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  {uploadingAttachment ? "Uploading..." : "Add attachment"}
                </button>
              </div>
              {attachmentError && <p className="text-xs text-[var(--cohort-clay)]">{attachmentError}</p>}
            </div>
            </fieldset>
          </div>

          {eventSaveError ? <p role="alert" className="mx-5 my-3 rounded-xl border border-[var(--cohort-clay-rule)] bg-[var(--cohort-clay-soft)] px-3 py-2 text-sm text-[var(--cohort-clay)] sm:mx-7">That didn&apos;t save. Your changes are still here. {eventSaveError}</p> : null}
          <div data-testid="event-editor-actions" className="cohort-event-editor__actions grid shrink-0 grid-cols-2 items-center gap-2.5 border-t border-[var(--cohort-rule)] bg-white px-5 pb-[22px] pt-3.5 sm:flex sm:px-7 sm:py-4">
            {eventEditorSaveGuidance ? (
              <p
                id="event-editor-save-guidance"
                data-testid="event-editor-save-guidance"
                className={`order-0 col-span-2 text-xs sm:sr-only ${eventValidationIssues.length > 0 ? "text-[var(--cohort-clay)]" : "text-[var(--cohort-ink-muted)]"}`}
              >
                {eventEditorSaveGuidance}
              </p>
            ) : null}
            {editingEvent.id !== "new" && (
              <Button
                variant="secondary"
                size="auto"
                className={`${eventEditorButtonClass} cohort-destructive-secondary-button order-3 w-full sm:order-1 sm:w-auto`}
                onClick={() => {
                  setCancelError("")
                  if (editingEvent.recurrenceSeriesId) {
                    setScopeChooserKind("cancel")
                  } else {
                    setCancelConfirmOpen(true)
                  }
                }}
                disabled={editorBusy}
              >
                Cancel meeting
              </Button>
            )}
            <span className="hidden flex-1 sm:block" />
            <Button variant="ghost" size="auto" onClick={closeDialogs} disabled={discardDisabled} className={`${eventEditorButtonClass} cohort-secondary-button order-2 w-full sm:order-2 sm:w-auto ${editingEvent.id === "new" ? "col-span-2" : ""}`}>
              {editingEvent.id === "new" ? "Discard" : "Discard changes"}
            </Button>
            <Button
              onClick={saveEvent}
              disabled={eventEditorSaveDisabled}
              aria-label={eventEditorSaveAriaLabel}
              aria-describedby={eventEditorSaveGuidance ? "event-editor-save-guidance" : undefined}
              title={saveDisabledBecauseUnchanged ? "Make a change to save" : undefined}
              className={`${eventEditorButtonClass} cohort-primary-button order-1 col-span-2 min-h-[52px] w-full text-sm sm:order-3 sm:min-h-[46px] sm:w-auto ${savingEvent ? "relative" : ""}`}
              size="auto"
            >
              {savingEvent ? (
                <>
                  <span className="invisible" aria-hidden="true">{eventEditorSaveLabel}</span>
                  <span className="absolute inset-0 flex items-center justify-center"><Spinner size="sm" /></span>
                </>
              ) : (
                eventEditorSaveLabel
              )}
            </Button>
          </div>
        </AnimatedDialog>
      )}

      {cancelConfirmOpen && editingEvent && editingEvent.id !== "new" && (
        <AnimatedDialog
          onClose={() => {
            if (!cancelling) setCancelConfirmOpen(false)
          }}
          closeDisabled={cancelling}
          className="cohort-dialog cohort-dialog--compact-sheet !w-[calc(100vw-32px)] !max-w-md !p-6 sm:!p-6"
        >
          <DialogHeader>
            <DialogTitle className="text-[22px] font-normal">Cancel this meeting?</DialogTitle>
            <DialogDescription>
              “{editingEvent.title || "This meeting"}” on {editingEvent.date}{editingEvent.time ? `, ${editingEvent.time}` : ""} will be removed from the schedule. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {cancelError && <p className="rounded-xl bg-[var(--cohort-clay-soft)] px-3 py-2 text-sm text-[var(--cohort-clay)]">{cancelError}</p>}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              size="auto"
              onClick={() => void cancelMeeting("occurrence")}
              disabled={cancelling}
              className="cohort-destructive-secondary-button w-full sm:w-auto"
            >
              {cancelling ? "Cancelling…" : "Yes, cancel it"}
            </Button>
            <Button
              type="button"
              size="auto"
              onClick={() => setCancelConfirmOpen(false)}
              disabled={cancelling}
              className="cohort-primary-button w-full sm:w-auto"
            >
              Keep meeting
            </Button>
          </div>
        </AnimatedDialog>
      )}

      {scopeChooserKind && (selectedEvent || editingEvent) && (
        <RecurrenceScopeChooser
          kind={scopeChooserKind}
          event={(selectedEvent || editingEvent) as EventsListItem}
          occurrenceStatus={(selectedEvent || editingEvent)?.occurrenceStatus}
          busy={cancelling}
          error={scopeChooserKind === "cancel" ? cancelError : ""}
          onDismiss={() => {
            if (!cancelling) setScopeChooserKind(null)
          }}
          onConfirm={async (scope) => {
            const event = selectedEvent || editingEvent
            if (!event) return
            if (scopeChooserKind === "edit") {
              setScopeChooserKind(null)
              openEditorForEvent(event, scope)
            } else {
              setEditingEvent(event)
              await cancelMeeting(scope, event)
            }
          }}
        />
      )}
    </>
  )
})

EventsListCard.displayName = "EventsListCard"

export default EventsListCard
